/** Desenul unui tronson — UNUL SINGUR, folosit și de vizualizator, și de editor.
 *
 *  DE CE E COMUN. Erau două: unul în `TronsonView` și unul în editor. Au apucat
 *  să difere înainte să apuce cineva să le compare — în vizualizator golurile
 *  erau desenate cu `pointerEvents="none"`, deci nu se putea da click pe nicio
 *  ușă, în timp ce în editor se putea. Două desene ale aceluiași lucru diverg
 *  întotdeauna, iar diferența se vede ca defect, nu ca alegere.
 *
 *  CONVENȚIA E CEA A PLANȘEI TEHNICE din `plan/StoreyView.tsx`: hârtie și tuș,
 *  zidul tăiat hașurat după ROLUL lui, nu o pată plină. Hașura e GEOMETRIE, nu
 *  `<pattern>` (v. `plan/hasura.ts`), și fiecare fel dispare la mărimea lui —
 *  oțelul la 5 cm se strânge într-un gri mult înainte ca zidăria de 14 să aibă
 *  vreo problemă, deci pragul e al distanței DE PE ECRAN.
 *
 *  Tabelul de hașuri e IMPORTAT, nu copiat: `structural` are 45° la 5 cm,
 *  `partition` 135° la 14, `fire` în cruce. Un al doilea tabel ar face ca
 *  același perete să se citească altfel în două ecrane ale aceluiași produs.
 *
 *  CE E SUB TĂIETURĂ NU E TĂIAT. Planul e o secțiune la 1,15 m; un parapet de
 *  1,05 nu e secționat de ea, deci nu poartă hașură și se conturează cu linia
 *  subțire («văzut»), nu cu cea groasă («tăiat») — exact regula din
 *  `NESECTIONAT`. Fără ea, un balcon cu parapet se citește ca o cameră închisă.
 */
import { useEffect, useId, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { tn, useT } from "../../i18n";
import { numeleCamerei, numeScurtCamerei } from "./feluri";
import { COTA_PX, coteleAsezariiGolului, cotaGolului, cotelePlanului,
         cutiaEtichetei, cutiaPiesei, cheiaCotei, type Cutie, type Cota,
         coteleDozelor, cutiaCifrei, cutiaLiniei, dozeleCuEtichetaLaDreapta,
         grosimileDozelor, type LantDoza }
  from "./cote";
import { cadrulDesenului, inInel, parapetiiDeSticla as parapetiiDeSticlaAi }
  from "./doc";
import { Cote } from "./CoteDesen";
import {
  caleHasura, HASURA_ARHITECTURALA, liniiHasura, MIN_PX_INTRE_LINII,
} from "../plan/hasura";
import type { FelHasura } from "../plan/hasura";
import { TUS } from "../plan/StoreyView";
import { cadru, PRAG_MOBILIER } from "../plan/arhMobilier";
import { podesteleScarii, ScaraDinRampe, partileScarii }
  from "../plan/ScaraDesen";
import { conturZonei } from "../plan/arhMaterial";
import { Oglinda, piesaPlan, piesaSingura, rangDesen }
  from "../plan/arhMobilierPlan";
import type { Balama, Gol, ModelUsa, PlanTronson, Prag, Pt } from "./doc";
import { grosimeaGazdeiPura } from "./doc";
import {
  finisajul, FinisajeDefs, patternul, rostVizibil,
} from "./design/finisaje";
import { Aparate, INK_FAMILIE, prinsaAparatului, Trasee } from "./design/ElectricDesen";
// `benzileStraturilor`, `caleaStratului` și `hasuraStratului` NU se mai
// importă: desenul pe straturi e stins deocamdată (v. `hasuri`, mai jos).
// Se pun la loc odată cu blocul comentat de-acolo.
// Tipurile de aici, operațiile din `taietor` — v. nota din `editor/camere`.
import type * as pc from "polygon-clipping";
import { taie } from "../taietor";
import type { Traseu } from "./design/electric";

/** Sub atâția pixeli de grosime, un strat nu se mai desenează deloc — v. nota
 *  de la desenul benzilor. O barieră de vapori are un milimetru: la mărirea la
 *  care se citește un modul întreg, ea și conturul zidului cad în același loc. */
const MIN_PX_BANDA = 1.5;

/** CE DESENEAZĂ UN PERETE ÎN LOC DE FOND. Una din două: hașura ROLULUI, pe un
 *  zid omogen, sau BENZILE straturilor, pe unul care își spune alcătuirea.
 *  Niciodată amândouă — a doua ar trece peste prima și s-ar citi ca un al
 *  treilea material. */
type HasuraPerete = {
  fel?: FelHasura;
  d?: string;
  benzi?: { pas: number; gros: number; inel: Pt[]; d: string }[];
};

/** Ce e selectat. Poziția în listele derivate — o cameră de tronson n-are
 *  niciun `id` în document, e un poligon dintr-o listă. */
export type Ales =
  | { fel: "perete"; i: number }
  | { fel: "camera"; i: number }
  | { fel: "gol"; fel2: "doors" | "windows"; i: number }
  | { fel: "mobila"; i: number }
  /** Un aparat electric — poziția lui în `plan.electrical`. */
  | { fel: "aparat"; i: number }
  /** O AXĂ DE TRASARE, ca poziție în `model::axele`.
   *
   *  Aleasă ca orice alt obiect, și tocmai de-aia: ștergerea, tasta `Delete`,
   *  bara de context și istoricul sunt deja scrise o dată, pe `Ales`. O axă cu
   *  butonul ei de șters pe planșă a fost prima încercare — un «×» lângă
   *  literă —, dar el stă tot timpul sub cursor și se apasă din greșeală. Ce se
   *  alege se și șterge, cu tasta cu care se șterge orice. */
  | { fel: "ax"; i: number }
  /** O COTĂ DE PE PLANȘĂ, după cheia ei stabilă (v. `cote::cheiaCotei`).
   *
   *  Aleasă ca orice alt obiect, din exact motivul scris mai sus pentru axă:
   *  ștergerea, tasta `Delete`, bara de context și istoricul sunt deja scrise
   *  o dată, pe `Ales`. Cota avea un «×» al ei pe planșă — aceeași primă
   *  încercare ca a axei —, iar arhitecta a cerut-o aleasă: «ar trebui să
   *  poată fi selectate și șterse cotele pe care le pune el automat»
   *  (26.10.2026).
   *
   *  PE CHEIE, NU PE INDICE, și asta e deosebirea față de toate celelalte: o
   *  cotă socotită nu are loc în document, se naște la fiecare desenare, iar
   *  poziția ei în listă se schimbă cu mărirea. Cheia se ia din FAȚA măsurată,
   *  care stă pe loc. */
  | { fel: "cota"; cheie: string }
  /** NUCLEUL de scară și lift, ca întreg — cutia lui. Nu poartă indice: un
   *  modul are cel mult unul (`doc.core`). */
  | { fel: "nucleu" }
  /** O bucată din nucleu: scara, un puț de lift, o ghenă. Poziția ei în
   *  `plan.core_parts` — sunt date citite de motoare, nu desen. */
  | { fel: "nucleuParte"; i: number }
  /** O BUCATĂ DIN SCARA DESENATĂ: o rampă sau un podest.
   *
   *  `i` e piesa din `plan.furniture`, `k` a câta rampă (sau al câtelea podest)
   *  din ea. Se aleg separat fiindcă se și spun separat: o rampă are trepte, un
   *  podest are adâncime, iar până acum singurul lucru care se putea apuca era
   *  scara întreagă. */
  | { fel: "scaraParte"; i: number; parte: "rampa" | "podest"; k: number }
  /** CONTURUL MODULULUI — banda albă care nu e a niciunui apartament.
   *
   *  Apăsată, deschidea nimic: era primul element din desen, fără mâner și fără
   *  panou, deci un clic pe ea stingea selecția. Acum spune ce îl guvernează —
   *  frontul, adâncimea benzii, brațele unui colț — fiindcă la întrebarea «cât
   *  de mare e tronsonul» nu răspundea niciun ecran. */
  | { fel: "anvelopa" }
  | null;

export const TUS_CULOARE = "#141414";
export const HARTIE = "#ffffff";
export const SEL = "#1668c4";
export const SEL_MOALE = "#7fb0e8";
/** NUMELE CAMEREI E TUȘ, nu cărămiziu. Planșele de referință (Stay House,
 *  MF Architecture) scriu totul cu același negru: numele cu majuscule, cifra
 *  dedesubt, mai mică. O a doua culoare pe planșă ar fi trebuit să însemne
 *  ceva, iar roșul nu însemna nimic — era doar moștenit de la `ApartmentView`. */
const ROOM_NAME = TUS_CULOARE;
const AREA_INK = TUS_CULOARE;

/** PLANȘA E ALB-NEGRU. Cerut anume: «tot ce e în plan să fie doar alb-negru,
 *  fără culori». Deci pe planșa tehnică nu există nicio suprafață colorată —
 *  nici somonul coridorului, nici spălătura camerelor ude. Pardoseala se
 *  citește din DESENUL rostului, în gri de tuș (`ROST_PLANSA`): scânduri la
 *  dormitor, dale la baie, grăunte la beton — ca pe planșa de referință, unde
 *  betonul are punctele lui chiar pe planul de arhitectură. Culorile
 *  finisajelor rămân ale vederii Design. Singura culoare care mai apare pe
 *  planșă e cea a SELECȚIEI, și ea nu e desen: e mâna. */
const ROST_PE_PLANSA = 0.9;

/** CÂT DE TARE SE VEDE PARDOSEALA ÎN DESIGN.
 *
 *  Pardoseala e FOND, nu desen: peste ea stau mobilierul, instalația, cotele și
 *  etichetele, și ele trebuie să se citească fără efort. La culoare plină,
 *  caroiajul unei dale de 60 sau lamelele unui parchet tăiau exact liniile
 *  drepte ale pieselor de deasupra — «nu prea se vede bine prin ea, în special
 *  la dale». Un singur strat translucid peste hârtia albă spală la fel TOATE
 *  finisajele, fondul cu rostul lui: culorile rămân deosebite între ele, dar
 *  niciuna nu mai concurează cu ce e desenat peste. Opacitatea stă pe GRUP,
 *  nu pe fiecare cale: rostul lăsat la culoare plină peste un fond spălat ar fi
 *  ieșit mai contrastant decât înainte, adică pe dos. */
const PODEA_IN_DESIGN = 0.65;

/** FONDUL ZIDULUI TĂIAT — poché, nu hârtie.
 *
 *  Zidul era alb ca și camera de lângă el: singurul lucru care-l despărțea de
 *  golul din jur era conturul, iar la mărirea la care se lucrează un contur de
 *  un pixel nu ține un plan. Convenția de planșă e poché-ul: partea tăiată de
 *  secțiune se umple mai închis decât hârtia, ca ochiul să prindă
 *  compartimentarea dintr-o privire, fără să citească linie cu linie.
 *
 *  GRI FOARTE DESCHIS, cerut anume așa («nu chiar alb, să fie puțin gri»), și
 *  nu mai închis de-atât fiindcă peste el se desenează hașura rolului și
 *  liniile straturilor, în tuș: un fond mai tare le-ar înghiți exact pe cele
 *  care spun ce fel de zid e. Rămâne alb-negru, deci nu strică regula planșei
 *  (v. `ROST_PE_PLANSA`) — e gri de tuș, nu culoare. */
export const ZID_FOND = "#ececec";

/** POCHÉUL ZIDULUI TĂIAT: plin, aproape negru.
 *
 *  Cerut de arhitect de DOUĂ ori — «toți pereții umpliți cu hașură neagră» pe
 *  19.10 dimineața, și iar seara, cu poză: «The walls are still lined and not
 *  just black hash (...) with a simple black hash not lines». Prima oară am
 *  răspuns cu o HAȘURĂ deasă pe tot; la mărirea de lucru ea se citește tot ca
 *  niște linii, fiindcă asta și e.
 *
 *  DE CE PLIN ȘI NU O HAȘURĂ ȘI MAI DEASĂ. Hașura e geometrie, nu `<pattern>`,
 *  și se stinge sub `MIN_PX_INTRE_LINII` (2,5 px între linii) ca să nu ajungă
 *  o pată. Un pas destul de mic cât să se citească PLIN trece pragul chiar la
 *  mărimea de lucru: probat la 3,5 cm pe un modul la 55 px/m — zidurile ies
 *  GOALE, albe. Deci «mai des» nu poate ajunge niciodată la «plin»: cele două
 *  cerințe se bat. Umplerea nu depinde de mărire, deci ține la orice zoom.
 *
 *  #1a1a1a, nu #000: pe hârtie un poché pur negru înghite conturul zidului și
 *  nu se mai vede unde se termină un perete și începe altul. */
export const ZID_POCHE = "#1a1a1a";

/** MODULUL DE CORTINĂ, în metri. Compoziția fațadei pune montanți la fiecare
 *  perete și muchie de placă, iar între ei module egale de 1,20–1,50 m
 *  (v. `fatada/prompt.py::curtain`). Aici, unde peretele e singur pe planșă,
 *  se ia mijlocul intervalului și se împarte lungimea în module egale. */
const CORTINA_MODUL = 1.35;
/** Lățimea montantului pe desen: 50 mm, cât profilul din sistemul real. */
const CORTINA_MONTANT = 0.05;

/** MONTANȚII UNUI PERETE-CORTINĂ: bare scurte de-a latul zidului, la fiecare
 *  rost dintre două panouri, PLUS cele două capete.
 *
 *  Se socotește din CONTUR, nu din ax, ca să meargă pe orice unghi: direcția
 *  lungă e a celei mai lungi laturi, iar lungimea și grosimea ies din
 *  proiecțiile vârfurilor pe ea și pe normala ei. Un zid întors la 30° are
 *  montanții tot de-a latul lui, nu pe est-vest.
 *
 *  ASTA E REPREZENTAREA CERUTĂ: sticla e două linii subțiri, iar panourile se
 *  despart prin montanți negri — «it looks like window sections separated by
 *  black lines» (20.10.2026). Un perete-cortină umplut plin s-ar citi ca
 *  zidărie portantă, iar hașurat ca o tăietură prin zidărie: el nu e nici
 *  una, nici alta. */
export function montantiiCortinei(inel: readonly Pt[]): string | null {
  if (inel.length < 3) return null;
  const c: Pt = [inel.reduce((a, q) => a + q[0], 0) / inel.length,
                 inel.reduce((a, q) => a + q[1], 0) / inel.length];
  // cea mai lungă latură dă direcția lungă
  let u: Pt = [1, 0];
  let cel = -1;
  for (let i = 0; i < inel.length; i++) {
    const a = inel[i], b = inel[(i + 1) % inel.length];
    const d = Math.hypot(b[0] - a[0], b[1] - a[1]);
    if (d > cel) { cel = d; u = [(b[0] - a[0]) / (d || 1), (b[1] - a[1]) / (d || 1)]; }
  }
  if (!(cel > 1e-6)) return null;
  const n: Pt = [-u[1], u[0]];
  const du = inel.map((q) => (q[0] - c[0]) * u[0] + (q[1] - c[1]) * u[1]);
  const dn = inel.map((q) => (q[0] - c[0]) * n[0] + (q[1] - c[1]) * n[1]);
  const [u0, u1] = [Math.min(...du), Math.max(...du)];
  const [n0, n1] = [Math.min(...dn), Math.max(...dn)];
  const L = u1 - u0;
  if (!(L > 1e-6)) return null;
  const cate = Math.max(1, Math.round(L / CORTINA_MODUL));
  const lat = Math.min(CORTINA_MONTANT, L / (cate * 2));
  let d = "";
  for (let k = 0; k <= cate; k++) {
    // capetele se trag ÎNĂUNTRU cu o jumătate de montant, ca bara să nu iasă
    // din zid: la rost montantul e centrat pe el, la capăt e lipit de față.
    const t = u0 + (L * k) / cate
      + (k === 0 ? lat / 2 : k === cate ? -lat / 2 : 0);
    const P = (su: number, sn: number): Pt =>
      [c[0] + u[0] * (t + su) + n[0] * sn, c[1] + u[1] * (t + su) + n[1] * sn];
    const q = [P(-lat / 2, n0), P(lat / 2, n0), P(lat / 2, n1), P(-lat / 2, n1)];
    d += `M${q.map((x) => `${x[0].toFixed(4)} ${x[1].toFixed(4)}`).join("L")}Z`;
  }
  return d;
}

/** Corpul numelui de cameră, în pixeli — v. nota din `etichete`. Numele e
 *  scrisul cel mai mare al planșei: cu două puncte peste cifra cotei. */
const ETICHETA_PX = COTA_PX.corp + 2;

/** Aria se scrie cu CORPUL COTEI: pe planșă e tot o măsură, deci stă la fel
 *  de mare ca ele, sub numele camerei. Raport, nu mărime fixă — pe o cameră
 *  strâmtă numele se micșorează, iar aria îl urmează.
 *
 *  CU UN PUNCT MAI MARE DECÂT COTA, de la 13.10.2026: cerut anume («make that
 *  text black not light gray and a bit bigger»). Ariile sunt cifrele pentru
 *  care se deschide planul; cotele sunt un lanț pe margine.
 *
 *  UN PUNCT, NU UN SFERT. Întâi a fost 1,25 × corpul cotei: 15 pe un nume de
 *  14, adică aria scrisă MAI MARE decât numele camerei — iar atunci nu mai e
 *  limpede ce se citește primul. Rămâne între ele: 13 pe un nume de 14. */
const ARIE_RAPORT = (COTA_PX.corp + 1) / ETICHETA_PX;
/** …și scrisă APĂSAT, nu subțire. La 500 pe fond alb, cifra de 7–8 px citea
 *  cenușiu lângă numele camerei, care e la 700 — «not light gray». E aceeași
 *  cerneală, dar un contur subțire la mărimea asta se stinge. */
const ARIE_GROSIME = 700;
/** Corpul ANTETULUI cu apartamentul, față de numele camerei. Mai mic decât aria
 *  și scris gri: e un titlu peste cameră, nu încă o cifră despre ea.
 *
 *  0,90, nu 0,74. Cerut de arhitect (19.10.2026): «Text for the apt type and
 *  area should be a bit bigger than it is now». Rămâne SUB numele camerei —
 *  ăla se citește primul, și un antet cât el ar face două titluri care se
 *  bat —, dar de la 0,74 el ieșea un rând de notă pe care trebuia să-l cauți.
 *  Nu e o cifră liberă: antetul se strânge oricum singur cât să încapă
 *  (v. `incape`), deci asta e MĂRIMEA DORITĂ, nu una garantată. */
const ANTET_RAPORT = (COTA_PX.corp / ETICHETA_PX) * 0.9;
/** Răsfirarea literelor antetului, în părți din corpul lui. */
const ANTET_RASFIRARE = 0.1;
/** Cât de lată iese o literă de antet: majusculă (ca numele camerei) plus
 *  răsfirarea, care se adaugă după fiecare literă. */
const FACTOR_ANTET = 0.68 + ANTET_RASFIRARE;
/** Sub atâția pixeli antetul nu se mai citește, deci nu se mai scrie. */
const ANTET_MIN_PX = 4.6;
/** Sub atâția pixeli nu se mai scrie NUMELE camerei — și atunci camera rămâne
 *  fără etichetă cu totul. Pragul la care se încearcă numele scurt. */
const PRAG_NUME_PX = 6.5;

/** UNDE STĂ ETICHETA: cel mai apropiat loc de mijlocul cadrului în care cutia
 *  ei (`lat` × `inalt`, în metri, axială) cade în cameră și nu atinge nicio
 *  piesă de mobilier. Candidații stau pe o grilă 7 × 7 peste cadrul înscris,
 *  cercetați dinspre mijloc spre margini. Fără cadru sau fără loc liber,
 *  mijlocul. */
function locLiber(
  cad: { cx: number; cy: number; w: number; h: number; deg: number } | null,
  inel: readonly Pt[], lat: number, inalt: number, mobila: readonly Cutie[],
): Pt {
  if (!cad) {
    const xs = inel.map((q) => q[0]);
    const ys = inel.map((q) => q[1]);
    return [(Math.min(...xs) + Math.max(...xs)) / 2, (Math.min(...ys) + Math.max(...ys)) / 2];
  }
  const rad = (cad.deg * Math.PI) / 180;
  const co = Math.cos(rad);
  const si = Math.sin(rad);
  // Cât poate aluneca centrul pe fiecare axă a cadrului ca cutia să rămână în el.
  const orizontal = Math.abs(co) > 0.5;
  const jocU = Math.max(0, cad.w - (orizontal ? lat : inalt)) / 2;
  const jocV = Math.max(0, cad.h - (orizontal ? inalt : lat)) / 2;
  const pasi = [0, -0.33, 0.33, -0.66, 0.66, -1, 1];
  const candidati: { p: Pt; d: number }[] = [];
  for (const fu of pasi) {
    for (const fv of pasi) {
      const u = fu * jocU;
      const v = fv * jocV;
      candidati.push({ p: [cad.cx + u * co - v * si, cad.cy + u * si + v * co],
                       d: u * u + v * v });
    }
  }
  candidati.sort((a, b) => a.d - b.d);
  const seAting = (a: Cutie, b: Cutie) =>
    a.x0 < b.x1 && b.x0 < a.x1 && a.y0 < b.y1 && b.y0 < a.y1;
  for (const { p } of candidati) {
    const cut: Cutie = { x0: p[0] - lat / 2, x1: p[0] + lat / 2,
                         y0: p[1] - inalt / 2, y1: p[1] + inalt / 2 };
    const colturi: Pt[] = [[cut.x0, cut.y0], [cut.x1, cut.y0], [cut.x1, cut.y1], [cut.x0, cut.y1]];
    if (!colturi.every((q) => inInel(q, inel))) continue;
    if (mobila.some((m) => seAting(m, cut))) continue;
    return p;
  }
  return [cad.cx, cad.cy];
}

/** Inel închis, cum îl cere `polygon-clipping` (ca în `editor/camere`). */
const inchis = (r: readonly Pt[]): Pt[] =>
  (r.length && (r[0][0] !== r[r.length - 1][0] || r[0][1] !== r[r.length - 1][1])
    ? [...r, r[0]] : [...r]);

/** INELUL CAMEREI REUNIT CU PETICELE EI — marginea a ce e podit.
 *
 *  Scos din componentă ca să poată fi probat: v. `conturulCamerei`, unde stă
 *  și de ce. Întoarce inelele reuniunii; la geometrie degenerată, inelul brut,
 *  fiindcă un contur aproximativ e mai bun decât niciunul. */
export function conturulPodit(inel: readonly Pt[],
                              petice: readonly (readonly Pt[])[]): Pt[][] {
  if (!petice.length) return [[...inel]];
  try {
    const u = taie().union([inchis(inel)] as pc.Polygon,
                           ...petice.map((r) => [inchis(r)] as pc.Polygon));
    const inele = u.flatMap((poly) => poly.map((r) => r as Pt[]));
    return inele.length ? inele : [[...inel]];
  } catch {
    return [[...inel]];
  }
}

export const cale = (ring: readonly Pt[]) =>
  `M${ring.map((p) => `${p[0]} ${p[1]}`).join("L")}Z`;

/** GROSIMEA FOII pe planșă: 4 cm. Blocul standard de ușă (ISO 7519, STAS 1434,
 *  blocurile de bibliotecă ale oricărui CAD) desenează foaia ca un DREPTUNGHI
 *  subțire, nu ca o linie: linia, trasă chiar la fața zidului și în același
 *  tuș, se citea ca un ciot de perete — «ușa se vede foarte slab». */
const FOAIE_GROSIME = 0.04;
/** Ușa de intrare în locuință, MAI APĂSATĂ: foaie mai groasă și tușul cel
 *  mai gros pe tot semnul, arc inclusiv (tocul ei mai lat vine din document,
 *  v. `doc.TOC_INTRARE`). Pe planșă ea spune unde începe locuința, deci
 *  trebuie găsită dintr-o privire între celelalte. */
const INTRARE = { foaie: 0.06, tus: TUS.taiat * 1.6 };

/** SEMNUL DE UȘĂ, SCOS DIN PLANȘA DE REFERINȚĂ.
 *
 *  Cerut cuvânt cu cuvânt pe 28.09.2026, cu planșa alături: «the door should
 *  look like this exactly like this extract the svg and use it (just the door
 *  and the line, the flooring is taken from the room we have)». Deci nu e
 *  desenat după ochi: cifrele de mai jos sunt geometria VECTORIALĂ a blocului
 *  din `layout.pdf` (Turtle Bay, rezidența 201–301), citită din fișier și
 *  adusă în sistemul ușii.
 *
 *  SISTEMUL: originea în balama, pe fața zidului dinspre care baleiază foaia;
 *  `x` spre celălalt montant, `y` spre partea în care se deschide; unitatea e
 *  LUNGIMEA FOII. Tot ce urmează e deci fără scară — se înmulțește cu foaia și
 *  iese la fel pe o ușă de 70 cm și pe una de un metru, exact ca blocul din
 *  care vine.
 *
 *  CE E ÎN EL: conturul foii ȘI clanța, un singur contur închis — așa e și în
 *  planșă, o singură cale trasată. Foaia stă la 25° de zid, nu la 90°: e cât
 *  se deschide o ușă în desenul de prezentare, și de-aia arcul punctat care o
 *  însoțește e scurt. (În planșă a doua ușă are 22,5°; unghiul e al
 *  desenatorului, nu al blocului, deci aici e unul singur, al ușii arătate.)
 *
 *  ARCUL e o curbă Bézier, nu un arc de cerc: tot așa e scris și în PDF. Are
 *  raza 0,995 — capătul închis al foii, adică montantul de vizavi. */
const BLOC_USA = {
  /** Conturul foii cu clanța, închis. */
  contur: [
    [0.8798, 0.3605], [0.9208, 0.3796], [0.9017, 0.4204], [0.8608, 0.4013],
    [0.8549, 0.4139], [0.8139, 0.3948], [0.7934, 0.4389], [0.7846, 0.4578],
    [0.6522, 0.3961], [0.661, 0.3772], [0.7745, 0.4301], [0.795, 0.386],
    [0.7541, 0.3669], [0.7599, 0.3543], [0, 0], [0.0191, -0.0409],
    [0.779, 0.3135], [0.7849, 0.3008], [0.8258, 0.32], [0.8464, 0.2758],
    [0.733, 0.223], [0.7418, 0.2041], [0.8741, 0.2658], [0.8653, 0.2847],
    [0.8447, 0.3288], [0.8857, 0.3479],
  ] as [number, number][],
  /** Arcul de baleiaj: punctul de pornire și cele trei ale curbei. */
  arc: [[0.9949, 0], [0.9949, 0.1452], [0.9631, 0.2888], [0.9017, 0.4204]] as
    [number, number][],
};

/** CÂT DE GROS E ZIDUL PE CARE STĂ UN GOL — mutată în `doc`, lângă geometrie.
 *
 *  A plecat de-aici (01.10.2026) fiindcă `deriva` are nevoie de ea ca să știe
 *  unde se oprește podeaua dintr-un gol de fereastră, iar `doc` nu poate
 *  importa din desen. Re-exportată, ca importurile de până acum să rămână. */
export { grosimeaGazdeiPura };

/** CÂT E PASUL PUNCTELOR DE PE ARC, ÎN PIXELI DE ECRAN.
 *
 *  În planșă e 3,9 % din foaie, adică o măsură a CLĂDIRII. Scris așa, la
 *  mărirea la care se vede tot tronsonul punctele se strâng sub un pixel și
 *  arcul dispare — s-a întâmplat deja o dată, și de-aia arcul ajunsese
 *  continuu. Tușul e oricum în pixeli (`vectorEffect`), deci și pasul se ia
 *  în pixeli: arcul rămâne punctat la orice mărire. */
const ARC_PUNCTE = "3 3";

/** Trei zecimale — un milimetru. Sub el, cifrele din cale sunt zgomot. */
const r3 = (v: number) => Math.round(v * 1000) / 1000;

/** Cum se ține un stilou pe un semn de ușă: foaia și tocul în tușul de tăiat,
 *  arcul — o mișcare, nu ceva tăiat — în tușul văzut. La intrare totul urcă. */
/** CÂT SE SUBȚIAZĂ TUȘUL LA DEPĂRTARE, ca factor.
 *
 *  Grosimile de planșă sunt în PIXELI constanți (v. `StoreyView::TUS` și
 *  `arhMobilierPlan::g`) — scrise în metri s-ar îngroșa la mărire și o
 *  canapea ar ieși mai grasă decât zidul din jurul ei. Constanta rezolvă însă
 *  o singură jumătate: la depărtare piesa se strânge, iar linia rămâne la
 *  1,2–1,5 px și ajunge să acopere chiar desenul pe care-l trasează. Așa
 *  arătau ușile și aragazul văzute de sus pe tot tronsonul — «prea groase».
 *
 *  Deci: nimic peste `REPER` (mărirea la care cifrele au fost alese), și
 *  proporțional sub el, cu o podea ca linia să nu dispară de tot. Zoom-ul
 *  înăuntru rămâne EXACT cum era.
 *
 *  REPERUL E 40 px/m fiindcă acolo au fost alese cifrele — e și mărirea
 *  implicită a planșei (`ApartmentView`), și cea pe care o fixează proba
 *  tușului de intrare (`gol.test.tsx`). La 40 factorul e fix 1, deci desenul
 *  de la mărirea de lucru iese neatins. */
const TUS_REPER = 40;
// PODEAUA, COBORÂTĂ LA 0,30 (01.10.2026). La 0,45 subțierea se oprea deja pe
// la 18 px/m, iar sub ea linia rămânea de 0,54 px oricât se depărta ochiul:
// semnul ușii și plita se citeau tot ca pete, pe un desen în care piesa are
// câțiva pixeli — «it still looks too thick when zoomed out a lot». 0,30 ține
// linia la o treime din tușul de lucru, adică vizibilă, și lasă subțierea să
// meargă până pe la 12 px/m, mărirea la care se vede un etaj întreg.
const TUS_PODEA = 0.3;
export const factorTus = (pxPerM: number): number =>
  Math.min(1, Math.max(TUS_PODEA, pxPerM / TUS_REPER));

export function tusulUsii(intrare: boolean): { taiat: number; miscare: number } {
  return intrare
    ? { taiat: INTRARE.tus, miscare: TUS.taiat }
    : { taiat: TUS.taiat, miscare: TUS.vazut };
}

/** SEMNUL UNEI UȘI, ca geometrie: căile SVG ale tocurilor, foilor, arcurilor
 *  și — la ușa glisantă — ale șinei cu săgeata ei. Pură, ca s-o deseneze la
 *  fel planșa (`Usa`) și fantoma din editor (`Panza`), și ca să se poată
 *  măsura în teste fără randare.
 *
 *  `a`, `b` stau pe AXUL zidului, la marginile golului; `s` e partea în care
 *  baleiază foaia, ca semn față de normala `(-uy, ux)`; `grosime` e a zidului;
 *  `toc` e lățimea tocului de-a lungul zidului, din document (`frame_m`).
 *
 *  TOCUL E UN VOLUM pe planșă — un dreptunghi pe toată grosimea zidului, la
 *  fiecare margine a golului —, nu o liniuță: liniuța se pierdea în capătul
 *  zidului tăiat, iar golul părea un zid rupt.
 *
 *  Toate cele trei modele împart tocurile și pornesc foaia de la FAȚA zidului
 *  (nu de la ax — pornită de la ax, traversa jumătate din zid și se citea ca
 *  desenată peste perete).
 *
 *  BALAMAUA STĂ LA CAPĂTUL ZIDULUI, pe marginea exterioară a tocului ei — cum
 *  o desenează planșele: foaia deschisă e lipită de capătul golului, iar
 *  arcul ei ajunge la fața interioară a tocului de vizavi. Până pe
 *  23.09.2026 balamaua stătea cu un toc mai înăuntru, iar Stefan a văzut-o
 *  greșit pe importul din imagine («trebuie puțin mai în exterior»): acolo
 *  golul vine cu tot cu tocuri, deci foaia pornea la 6 cm de zid. Foaia
 *  simplă are deci lumina plus un toc; cele două foi ale ușii duble, câte o
 *  jumătate de gol. */
export function simbolUsa(o: {
  a: Pt; b: Pt; latime: number; s: 1 | -1; grosime: number; toc: number;
  model: ModelUsa; intrare: boolean;
  /** Tocul balamalei; lipsă = `start`, adică `a`. */
  balama?: Balama;
}): { tocuri: string[]; foi: string[]; arce: string[]; sina: string | null;
      balamale: string[] } {
  // BALAMAUA ÎN CELĂLALT TOC = același semn, în oglindă: se schimbă `a` cu
  // `b` și, fiindcă asta întoarce direcția golului deci și normala, se
  // întoarce și `s`, ca foaia să baleieze tot în aceeași parte a zidului.
  if (o.balama === "end") {
    return simbolUsa({ ...o, a: o.b, b: o.a, s: o.s === 1 ? -1 : 1, balama: "start" });
  }
  const { a, b, latime, s, grosime, toc, model, intrare } = o;
  const ux = (b[0] - a[0]) / (latime || 1);
  const uy = (b[1] - a[1]) / (latime || 1);
  const nx = -uy;
  const ny = ux;
  const t = grosime / 2;
  const f = intrare ? INTRARE.foaie : FOAIE_GROSIME;
  const patrat = (p: Pt, q: Pt, r: Pt, w: Pt) =>
    `M${p[0]} ${p[1]}L${q[0]} ${q[1]}L${r[0]} ${r[1]}L${w[0]} ${w[1]}Z`;
  // dreptunghiul cu colțul în `p`, latura `d1` apoi latura `d2`
  const drept = (p: Pt, d1: Pt, d2: Pt) => patrat(
    p, [p[0] + d1[0], p[1] + d1[1]],
    [p[0] + d1[0] + d2[0], p[1] + d1[1] + d2[1]], [p[0] + d2[0], p[1] + d2[1]]);

  // TOCURILE: pe toată grosimea zidului, late de `toc`, la fiecare margine
  const tocuri = [
    drept([a[0] - nx * t, a[1] - ny * t], [ux * toc, uy * toc], [nx * 2 * t, ny * 2 * t]),
    drept([b[0] - nx * t, b[1] - ny * t], [-ux * toc, -uy * toc], [nx * 2 * t, ny * 2 * t]),
  ];

  // LUMINA UȘII, pe fața dinspre care baleiază foaia
  const L = Math.max(latime - 2 * toc, 0.1);
  const fx = nx * s * t;
  const fy = ny * s * t;
  const A: Pt = [a[0] + ux * toc + fx, a[1] + uy * toc + fy];
  const B: Pt = [b[0] - ux * toc + fx, b[1] - uy * toc + fy];

  const foi: string[] = [];
  const arce: string[] = [];
  const balamale: string[] = [];
  let sina: string | null = null;
  // BLOCUL DIN PLANȘA DE REFERINȚĂ, așezat pe ușa asta — v. `BLOC_USA`.
  //
  // `h` e balamaua, `dir` spune încotro merge axa `x` a blocului: `1` spre
  // celălalt montant, `-1` înapoi (a doua foaie a unei uși duble, care e
  // oglinda primeia). `l` e lungimea foii, adică unitatea blocului.
  const asezat = (h: Pt, l: number, dir: 1 | -1) => {
    const px = (q: readonly number[]): Pt => [
      h[0] + ux * dir * q[0] * l + nx * s * q[1] * l,
      h[1] + uy * dir * q[0] * l + ny * s * q[1] * l,
    ];
    const c = BLOC_USA.contur.map(px);
    const [a0, c1, c2, a1] = BLOC_USA.arc.map(px);
    return {
      foaie: `M${c.map((q) => `${r3(q[0])} ${r3(q[1])}`).join("L")}Z`,
      arc: `M${r3(a0[0])} ${r3(a0[1])}C${r3(c1[0])} ${r3(c1[1])},`
        + `${r3(c2[0])} ${r3(c2[1])},${r3(a1[0])} ${r3(a1[1])}`,
    };
  };

  if (model === "single") {
    // FOAIA E CÂT LUMINA ȘI PORNEȘTE DIN EA, nu cât golul.
    //
    // Era cât GOLUL — de la muchia de afară a unui toc la a celuilalt —, adică
    // mai lată cu două tocuri decât ușa pe care o închide, și prinsă cu balamaua
    // pe muchia dinafară a tocului ei. Pe un gol de 0,90 cu tocuri de 8 cm asta
    // înseamnă o foaie de 0,90 într-o deschidere de 0,74: ieșea peste toc la
    // amândouă capetele, iar arcul se termina pe tocul de vizavi în loc să
    // ajungă în deschidere. Se vedea ca o ușă care nu se potrivește în golul ei
    // — «make sure it lines up with the doorway».
    //
    // O foaie e cât gaura pe care o umple: se prinde în `A` — capătul luminii
    // dinspre balama, pe fața pe care baleiază — și e lungă cât `L`.
    const u = asezat(A, L, 1);
    foi.push(u.foaie);
    arce.push(u.arc);
  } else if (model === "double") {
    // DOUĂ FOI care se întâlnesc la mijlocul luminii, fiecare cu clanța ei —
    // același bloc, o dată de la fiecare capăt al deschiderii, a doua în
    // oglindă. Jumătate de lumină fiecare, din același motiv ca mai sus.
    const l = L / 2;
    const p1 = asezat(A, l, 1);
    const p2 = asezat(B, l, -1);
    foi.push(p1.foaie, p2.foaie);
    arce.push(p1.arc, p2.arc);
  } else {
    // GLISANTĂ: foaia alunecă DE-A LUNGUL zidului, pe fața `s`, și în poziția
    // deschisă stă jumătate în lumină, jumătate peste zid, lângă tocul `b`.
    // Șina e linia luminii, iar săgeata spune încotro se trage ca s-o închidă.
    const dep = 0.02;                              // rostul dintre foaie și zid
    const P: Pt = [A[0] + ux * L / 2 + nx * s * dep, A[1] + uy * L / 2 + ny * s * dep];
    foi.push(drept(P, [ux * L, uy * L], [nx * s * f, ny * s * f]));
    const cap = Math.min(0.12, L / 4);
    const varf: Pt = [A[0] + ux * cap, A[1] + uy * cap];
    const de: Pt = [A[0] + ux * L / 2, A[1] + uy * L / 2];
    sina = `M${A[0]} ${A[1]}L${B[0]} ${B[1]}`
      + `M${de[0]} ${de[1]}L${varf[0]} ${varf[1]}`
      + `M${varf[0] + ux * cap * 0.6 + nx * cap * 0.4} ${varf[1] + uy * cap * 0.6 + ny * cap * 0.4}`
      + `L${varf[0]} ${varf[1]}`
      + `L${varf[0] + ux * cap * 0.6 - nx * cap * 0.4} ${varf[1] + uy * cap * 0.6 - ny * cap * 0.4}`;
  }
  // BALAMAUA, ca o jumătate de disc pe fața zidului.
  //
  // Un semn de ușă spune pe unde se intră și cât loc mănâncă foaia; nu spunea
  // însă NIMIC despre cum e prinsă — foaia pornea dintr-un punct gol. Discul e
  // convenția de tâmplărie pentru pivot, mic cât tocul, așezat exact pe muchia
  // de care se prinde foaia și bombat spre partea în care ea baleiază. Cerut:
  // «add a small half cylinder to the connection between the wall and door».
  if (model !== "sliding") {
    const r = Math.min(0.032, Math.max(0.018, toc / 2));
    const capete: [Pt, 1 | -1][] = model === "double"
      ? [[A, 1], [B, -1]] : [[A, 1]];
    for (const [h, dir] of capete) {
      const p0: Pt = [h[0] - ux * dir * r, h[1] - uy * dir * r];
      const p1: Pt = [h[0] + ux * dir * r, h[1] + uy * dir * r];
      // arcul se bombează spre `s`, adică în camera în care se deschide
      const sens = s * dir > 0 ? 1 : 0;
      balamale.push(`M${r3(p0[0])} ${r3(p0[1])}`
        + `A${r3(r)} ${r3(r)} 0 0 ${sens} ${r3(p1[0])} ${r3(p1[1])}Z`);
    }
  }
  return { tocuri, foi, arce, sina, balamale };
}

/** Ușa, deschisă la 90°, cu arcul ei de baleiaj — convenția de planșă: arată
 *  cât spațiu îi trebuie, adică exact ce se verifică pe un plan. `grosime` e a
 *  zidului-gazdă: pe grosimea lui se desenează tocurile, de la fața lui
 *  pornește foaia. Geometria e a lui `simbolUsa`; aici e doar tușul.
 *
 *  SIMBOLUL E TRANSPARENT: foaia e doar contur (`fill="none"`), arcul e doar
 *  linie. Prin ușă se vede pardoseala camerei, ca prin golul din mască.
 *  Singurul lucru plin e TOCUL, și el e plin cu hârtie fiindcă E tăiat de
 *  planul secțiunii — ca zidul în care stă. */
export function Usa({ g, grosime, gros = 1 }: {
  g: Gol; grosime: number;
  /** Factorul de tuș al măririi — v. `factorTus`. */
  gros?: number;
}) {
  const ux = (g.b[0] - g.a[0]) / (g.latime || 1);
  const uy = (g.b[1] - g.a[1]) / (g.latime || 1);
  // UȘA SE DESENEAZĂ MEREU DESCHISĂ, cu arcul de baleiaj. Cerut așa: pe planșă
  // arcul e ce spune pe unde se intră și cât loc mănâncă foaia, iar o ușă
  // închisă e o linie din care nu se citește nimic. `spre` lipsă nu mai
  // schimbă nimic: când regula care ghicește partea (v. `spreCamera`) tace —
  // o ușă între două camere la fel de bune — se alege o parte și se desenează.
  // Partea e o convenție de planșă, iar cine o vrea altfel o întoarce.
  // NU SE ÎNCHIDE LA CLICK — o stare de desen care se pierdea la fiecare
  // deschidere și fura clicul de selecție. Sensul se întoarce din bara de
  // context; deschis/închis nu există.
  const s = g.spre ? semnul(g, -uy, ux) : 1;
  const semn = simbolUsa({ a: g.a, b: g.b, latime: g.latime, s, grosime,
                           toc: g.toc, model: g.model, intrare: g.intrare,
                           balama: g.balama });
  const t0 = tusulUsii(g.intrare);
  const tus = { taiat: t0.taiat * gros, miscare: t0.miscare * gros };
  return (
    <>
      {/* TOCURILE NU SE MAI DESENEAZĂ. Erau două dreptunghiuri ALBE cu contur,
          de-a curmezișul golului la capetele lui: pe hârtie albă nu se vedeau,
          dar de când pardoseala fiecărei camere intră în deschidere ele au
          devenit o cutie desenată peste ea, cu două dungi albe la margini.
          Cerut scoase (28.09.2026): «the door should only have the middle line
          and not the box outline and there should be no white lines at the
          edges».

          CE RĂMÂNE ÎN DESCHIDERE: pardoseala fiecărei jumătăți și linia de pe
          ax — adică exact ce spune unde se schimbă finisajul. Foaia și arcul
          de baleiaj rămân și ele: ele stau ÎN CAMERĂ, nu în gol, și sunt
          singurele care spun pe unde se intră.

          Fereastra își păstrează tocurile: acolo ele închid rama geamului, iar
          în golul unei ferestre nu intră nicio pardoseală. */}
      {/* TOCURILE SE DESENEAZĂ IAR, DAR NUMAI CU LINIA.
          Erau dreptunghiuri PLINE cu alb, iar de când pardoseala intră în
          deschidere ele au devenit o cutie albă peste ea, cu două dungi la
          margini — de-aia au fost scoase pe 28.09. Fără ele însă, foaia
          pornea din gol: «the door is floating». Conturul le aduce înapoi pe
          amândouă socotelile: se vede de ce se prinde foaia, iar pardoseala
          trece neîntreruptă pe sub el, fiindcă nu se umple nimic. */}
      {semn.tocuri.map((d, i) => (
        <path key={`t${i}`} d={d} fill="none"
              stroke={TUS_CULOARE} strokeWidth={tus.miscare}
              strokeLinejoin="miter" vectorEffect="non-scaling-stroke" />
      ))}
      {/* BALAMAUA — jumătatea de disc de pe muchia de care se prinde foaia. */}
      {semn.balamale.map((d, i) => (
        <path key={`b${i}`} d={d} fill={TUS_CULOARE} fillOpacity={0.55}
              stroke={TUS_CULOARE} strokeWidth={tus.miscare}
              vectorEffect="non-scaling-stroke" />
      ))}
      {/* FOAIA: dreptunghi, doar contur. La mărirea la care se vede tot
          tronsonul cele două muchii se lipesc într-o bară groasă — exact cum
          iese blocul de ușă la imprimare — iar de aproape se desface. */}
      {semn.foi.map((d, i) => (
        <path key={`f${i}`} d={d} fill="none"
              stroke={TUS_CULOARE} strokeWidth={tus.taiat}
              strokeLinejoin="miter" vectorEffect="non-scaling-stroke" />
      ))}
      {/* ARCUL DE BALEIAJ, PUNCTAT — ca în planșa de referință (v. `BLOC_USA`).
          A mai fost punctat o dată, cu pasul în METRI, și dispărea la scara
          planului: punctele se strângeau sub un pixel. Pasul e acum în pixeli
          de ecran (`ARC_PUNCTE`), ca și tușul, deci ține la orice mărire. */}
      {semn.arce.map((d, i) => (
        <path key={`a${i}`} d={d} fill="none"
              stroke={TUS_CULOARE} strokeWidth={tus.miscare}
              strokeDasharray={ARC_PUNCTE}
              vectorEffect="non-scaling-stroke" />
      ))}
      {semn.sina && (
        <path d={semn.sina} fill="none"
              stroke={TUS_CULOARE} strokeWidth={tus.miscare}
              strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
      )}
    </>
  );
}

export function Fereastra({ g, grosime }: { g: Gol; grosime: number }) {
  const ux = (g.b[0] - g.a[0]) / (g.latime || 1);
  const uy = (g.b[1] - g.a[1]) / (g.latime || 1);
  const nx = -uy;
  const ny = ux;
  const t = grosime / 2;
  // LINIILE GEAMULUI CAD PE FEȚELE ZIDULUI, nu undeva înăuntru.
  //
  // Au stat întâi la ±5 cm de ax, apoi la 62 % din jumătatea grosimii. Și-ntr-un
  // caz și-n altul rămânea hârtie albă între linie și fața zidului, iar golul
  // părea mai îngust decât e: nu se vedea unde se termină fereastra și unde
  // începe zidul. Cerut: «the black lines mark the edges of the window and
  // there is no white space outside of those edges».
  //
  // Deci linia e chiar fața: `e = t`. Tocurile, desenate de-a curmezișul tot
  // până la `t`, le închid la capete — semnul iese o ramă închisă, cât golul.
  //
  // ȘI DOUĂ LINII ÎNĂUNTRU, la o treime și la două treimi: rama singură se
  // citea ca un gol gol, iar semnul de fereastră de pe planșă are foaia
  // desenată. Patru linii, trei fâșii — cum arată și în referința cerută.
  const e = t;
  return (
    <>
      {/* tocurile, de-a curmezișul zidului — ca la ușă */}
      {[g.a, g.b].map((q, i) => (
        <path key={`t${i}`}
              d={`M${q[0] - nx * t} ${q[1] - ny * t}L${q[0] + nx * t} ${q[1] + ny * t}`}
              stroke={TUS_CULOARE} strokeWidth={TUS.taiat} fill="none"
              vectorEffect="non-scaling-stroke" />
      ))}
      {[e, e / 3, -e / 3, -e].map((d, i) => (
        <path key={i} fill="none" stroke={TUS_CULOARE} strokeWidth={TUS.subtire}
              vectorEffect="non-scaling-stroke"
              d={`M${g.a[0] + nx * d} ${g.a[1] + ny * d}L${g.b[0] + nx * d} ${g.b[1] + ny * d}`} />
      ))}
    </>
  );
}

/** Semnul baleiajului: în ce parte a normalei se deschide. Din `g.spre`, care
 *  e deja fie sensul SCRIS în document, fie cel dedus (v. `doc.deriva`). */
const semnul = (g: Gol, nx: number, ny: number): 1 | -1 =>
  (g.spre && Math.sign((g.spre[0] - g.c[0]) * nx + (g.spre[1] - g.c[1]) * ny) < 0
    ? -1 : 1);

/** Cât iese ȘTERSĂTURA dincolo de fețele zidului pe care îl taie.
 *
 *  Trebuie să iasă puțin: conturul zidului e desenat înaintea ei, iar o
 *  ștersătură exact cât grosimea l-ar lăsa să treacă de-a curmezișul golului —
 *  o ușă care arată zidită. Doi centimetri ajung și nu se văd. */
const IESIRE_STERSATURA = 0.02;

/** Grosimea presupusă când golul nu-și spune peretele. 0,30 m e cel mai des
 *  întâlnit din catalog (221 din 401 de ziduri), deci greșește rar și, când
 *  greșește, greșește în plus — un gol acoperit e mai bun decât unul din care
 *  se vede zidul. În catalogul de azi nu se ajunge aici: toate cele 122 de
 *  goluri își numesc gazda. */
// `GROSIME_NESTIUTA` a plecat în `doc`, odată cu `grosimeaGazdeiPura`.


/** ȘTERSĂTURA UNUI GOL: dreptunghiul alb care taie zidul, ca golul să se vadă
 *  gol. Cât zidul plus o ieșire mică — v. `IESIRE_STERSATURA`. */
export function stersaturaGolului(g: Gol, grosime: number): Pt[] {
  const ux = (g.b[0] - g.a[0]) / (g.latime || 1);
  const uy = (g.b[1] - g.a[1]) / (g.latime || 1);
  const T = grosime / 2 + IESIRE_STERSATURA;
  return [
    [g.a[0] - uy * T, g.a[1] + ux * T], [g.b[0] - uy * T, g.b[1] + ux * T],
    [g.b[0] + uy * T, g.b[1] - ux * T], [g.a[0] + uy * T, g.a[1] - ux * T]];
}
/** ȘTERSĂTURA DUSĂ PÂNĂ LA CAMERE — masca prin care trec zidurile ȘI contururile.
 *
 *  Cât zidul nu ajunge. Inelul unei camere nu se lipește neapărat de fața
 *  zidului: pe M1, la ușa dintre hol și baie, zidul W46 are 30 cm iar camerele
 *  de-o parte și de alta stau la 8 cm de fețele lui. Masca tăia atunci numai
 *  banda zidului, iar conturul fiecărei camere trecea DREPT PRIN DESCHIDERE —
 *  două linii paralele peste pardoseala care tocmai intrase acolo. Cu tocurile
 *  scoase, ele au rămas singurele linii de-a curmezișul golului și se citeau
 *  ca o cutie: «WHY ARE YOU STILL PUTTING THAT BOX OUTLINE OF THE DOORWAY»
 *  (28.09.2026).
 *
 *  Deci masca merge până unde merge PARDOSEALA: la fața camerei, adică exact
 *  cât pragul ei (v. `doc::pragurileUsilor`). Fiecare parte cu adâncimea ei —
 *  cele două camere pot sta la depărtări diferite de zid —, și niciodată mai
 *  puțin decât zidul, ca un gol fără praguri (o ușă spre exterior) să rămână
 *  tăiat ca până acum. */
export function masculGolului(g: Gol, grosime: number,
                              praguri: readonly Prag[]): Pt[] {
  const L = g.latime || 1;
  const ux = (g.b[0] - g.a[0]) / L;
  const uy = (g.b[1] - g.a[1]) / L;
  const nx = -uy;
  const ny = ux;
  const minim = grosime / 2 + IESIRE_STERSATURA;
  let sus = minim;
  let jos = minim;
  // …ȘI PE LĂȚIME CÂT PRAGURILE. Scobitura camerei e mai lată decât golul —
  // 1,00 m la un gol de 0,90 pe M1 —, deci o mască cât golul lăsa afară
  // colțurile ei: două cioturi de contur de fiecare parte, adică tocmai
  // cutia care s-a cerut scoasă.
  let t0 = 0;
  let t1 = L;
  for (const q of praguri) {
    // CÂT DE DEPARTE DE AX ajunge peticul, nu cât e el de gros. Erau același
    // lucru cât timp peticul pleca de pe ax — la ușă așa e. Glaful ferestrei
    // pleacă însă de pe FAȚA zidului (v. `doc::pervazurileFerestrelor`), deci
    // grosimea lui e doar scobitura, iar masca ieșea cât zidul și lăsa pe
    // desen chiar treapta pe care trebuia s-o șteargă.
    const h = (q.inel[3][0] - g.c[0]) * nx
            + (q.inel[3][1] - g.c[1]) * ny;
    // …plus aceeași IEȘIRE ca pe grosimea zidului, și din exact același motiv:
    // conturul camerei cade CHIAR pe marginea pragului, iar o mască oprită
    // acolo îi taie doar jumătatea dinăuntru — cealaltă jumătate de linie
    // rămâne pe desen și cutia se vede mai departe, doar mai subțire.
    if (h > 0) sus = Math.max(sus, h + IESIRE_STERSATURA);
    else jos = Math.max(jos, -h + IESIRE_STERSATURA);
    for (const v of q.inel) {
      const t = (v[0] - g.a[0]) * ux + (v[1] - g.a[1]) * uy;
      t0 = Math.min(t0, t - IESIRE_STERSATURA);
      t1 = Math.max(t1, t + IESIRE_STERSATURA);
    }
  }
  const A: Pt = [g.a[0] + ux * t0, g.a[1] + uy * t0];
  const B: Pt = [g.a[0] + ux * t1, g.a[1] + uy * t1];
  return [
    [A[0] + nx * sus, A[1] + ny * sus], [B[0] + nx * sus, B[1] + ny * sus],
    [B[0] - nx * jos, B[1] - ny * jos], [A[0] - nx * jos, A[1] - ny * jos],
  ];
}


/** ZONA DE APUCAT A UNUI GOL.
 *
 *  ERA CÂT BALEIAJUL, ȘI DE-AIA FURA CLICKURI. Zona ținea tot pătratul de
 *  latura ușii în partea în care se deschide — aproape 0,85 m de suprafață
 *  nevăzută, peste cameră, peste mobilă și peste zidurile de lângă. Apăsai în
 *  camera de lângă ușă ca s-o alegi și se alegea ușa; mai rău, apăsarea o și
 *  DESCHIDEA sau o închidea, adică punea un pas în istoric și marca documentul
 *  nesalvat pentru un gest care nu voia să atingă ușa. Ăsta e „interferează cu
 *  alte obiecte" din feedback.
 *
 *  Acum zona e TOCUL, lățit cu câțiva pixeli ca să rămână de nimerit la orice
 *  mărire. Baleiajul intră în ea abia după ce ușa e ALEASĂ — atunci apăsarea în
 *  dreptul arcului nu mai poate fi despre altceva, iar zona mare e chiar ce
 *  trebuie ca ușa să se poată trage de-a lungul zidului. */
export function zonaGolului(g: Gol, grosime: number,
                            selectat: boolean): Pt[] {
  const ux = (g.b[0] - g.a[0]) / (g.latime || 1);
  const uy = (g.b[1] - g.a[1]) / (g.latime || 1);
  const nx = -uy;
  const ny = ux;
  // ZONA NU IESE DIN ZID, și de-aia: lățită cu câțiva pixeli de fiecare parte,
  // ea intra în camerele de alături și, fiind desenată PESTE pereți, fura
  // clicul de pe zid — «dau click pe un perete și îmi selectează fereastra din
  // acel perete». Golul e destul de lat ca să fie nimerit fără ajutor: o ușă are
  // 0,90 m, adică 30 px chiar la mărirea la care se vede tot modulul.
  //
  // Rămâne deci exact cât zidul. Cine vrea zidul apasă pe zid, cine vrea golul
  // apasă pe gol, iar cele două nu se mai calcă.
  const gros = grosime / 2;
  const s = selectat && g.fel === "usa" && g.spre
    ? semnul(g, nx, ny) : 0;
  const inainte = s === 0 ? gros : g.latime * 0.95;
  const inapoi = gros;
  const f = s >= 0 ? inainte : inapoi;
  const b = s >= 0 ? inapoi : inainte;
  return [
    [g.a[0] + nx * f, g.a[1] + ny * f], [g.b[0] + nx * f, g.b[1] + ny * f],
    [g.b[0] - nx * b, g.b[1] - ny * b], [g.a[0] - nx * b, g.a[1] - ny * b],
  ];
}

/** MOBILIERUL, din aceeași bibliotecă pe care o desenează planul de arhitectură
 *  (`plan/arhMobilierPlan.tsx`). Nu e decor: o cameră de 18 m² în care nu încap
 *  o canapea și o masă, sau un dormitor de 12 în care nu intră un pat cu loc pe
 *  lângă el, e o cameră care trece pe arie și cade la folosire. Desenul e
 *  singurul loc unde asta se vede.
 *
 *  `cadru` dă dreptunghiul INTERIOR al camerei — cel mai mare care încape chiar
 *  înăuntru, nu cadrul de încadrare —, ca mobila unei camere în L să nu iasă pe
 *  brațul scurt. Se importă și el, nu se rescrie: e aceeași socoteală.
 *
 *  SUB UN PRAG NU SE DESENEAZĂ. La mărirea la care se citește un tronson întreg,
 *  o canapea are câțiva pixeli și devine o pată — `PRAG_MOBILIER` e latura
 *  scurtă a camerei, în pixeli, sub care biblioteca tace. */
export function Mobila({ plan, pxPerM, ales, mobilaAlese, apuca, interactiv }: {
  plan: PlanTronson; pxPerM: number; ales: Ales;
  mobilaAlese?: readonly number[];
  apuca: (a: Ales) => (e: React.PointerEvent) => void;
  interactiv: boolean;
}) {
  return (
    // Tot stratul primește factorul o dată — v. `arhMobilierPlan::g`.
    <g pointerEvents="none"
       style={{ "--mob-gros": factorTus(pxPerM) } as CSSProperties}>
      {/* NUMAI MOBILA SCRISĂ ÎN DOCUMENT. Cea dedusă din felul camerei —
          canapea în living, pat în dormitor — nu se mai desenează nicăieri
          (Stefan, 23.09.2026: «dacă generatorul nu pune mobila atunci nu o mai
          pune nicăieri»): nu se putea apuca și nu exista pentru niciun motor.
          Fiecare piesă își poartă dreptunghiul și unghiul ei, și se poate
          apuca, fiindcă cine a pus-o vrea s-o și mute. */}
      {/* ORDINEA DE DESENARE: covoarele dedesubt, blaturile peste ele,
          aparatele peste blaturi (v. `arhMobilierPlan::rangDesen`).
          Pe planșă covorul stă SUB pat și sub măsuță — acolo unde se
          suprapun se vede piesa de deasupra, nu covorul (Bianca, 12.10.2026:
          «the rug should be under the furniture like in the picture»). În SVG
          «deasupra» înseamnă «desenat mai târziu», iar piesele veneau în
          ordinea din document, adică în ordinea în care le-a citit
          cititorul: un covor transcris după pat îl acoperea.
          SE SORTEAZĂ DOAR DESENUL, nu documentul: indicele `i` rămâne cel din
          `plan.piese`, fiindcă pe el merge selecția și orice gest de pe urmă.
          Covoarele nu se mai despart de mobila de peste ele nici la import
          (v. `finalizare::fara_suprapuneri`).
          RANGUL SCRIS BATE RANGUL FELULUI (v. `PiesaDoc.rang`): «Adu în față»
          și «Trimite în spate» sunt hotărârea omului despre ce vrea să vadă
          deasupra, iar o regulă pe fel n-are cum s-o ghicească. Sortarea e
          stabilă, deci două piese de același rang rămân în ordinea
          documentului — exact cum erau înainte să existe câmpul. */}
      {plan.piese
        .map((f, i) => [f, i] as const)
        .sort((a, b) => (a[0].rang ?? rangDesen(a[0].kind))
          - (b[0].rang ?? rangDesen(b[0].kind)))
        .map(([f, i]) => {
        if (Math.min(f.w, f.h) * pxPerM < PRAG_MOBILIER) return null;
        // Întâi ca piesă singură; dacă felul nu e una, ca mobilare de cameră —
        // așa rămân desenate și piesele scrise înainte ca biblioteca de obiecte
        // să existe.
        // SCARA ÎȘI IA CONTRATREPTELE DIN NIVEL, nu din cutia ei — v.
        // `plan/scara.ts`. Pentru restul pieselor argumentul nu înseamnă nimic.
        //
        // TRASEUL DE EVACUARE NU SE MAI DESENEAZĂ. Semielipsele punctate de pe
        // podeste arătau pe unde se întoarce omul care coboară; pe o scară cu
        // patru rampe ele acoperă capetele casei scării și se citesc ca niște
        // arce de ușă sau ca o a doua geometrie peste trepte. Cerut: «remove
        // the dotted lines like the evacuation lines from the stairs cause
        // they are confusing» (08.10.2026). `ScaraDinRampe` le știe mai
        // departe, pe `traseu` — nimeni nu i-l mai dă.
        //
        // SCARA TRASĂ se desenează din RAMPELE ei, nu din cutie: cutia e
        // rezultatul lor. Rampele sunt scrise în coordonatele planului, iar
        // piesa se desenează într-un cadru mutat în centrul ei — deci se aduc
        // în cadrul ăla scăzând centrul. Rotirea de deasupra le prinde la fel
        // ca pe orice piesă.
        // Scara se desenează în cadrul piesei (mutat în centrul ei), deci
        // tot ce e scris în coordonatele planului se aduce scăzând centrul.
        const adu = (q: Pt): Pt => [q[0] - f.c[0], q[1] - f.c[1]];
        const piesa = f.scara
          ? <ScaraDinRampe
              rampe={f.scara.rampe.map((r) => ({
                de: [r.de[0] - f.c[0], r.de[1] - f.c[1]] as [number, number],
                la: [r.la[0] - f.c[0], r.la[1] - f.c[1]] as [number, number],
              }))}
              latime={f.scara.latime_m} hNivel={plan.nivel}
              adancPodest={f.scara.podest_m}
              podeste={podesteleScarii(f.scara, adu)}
              treapta={f.scara.treapta_m} pas={f.scara.pas_m} />
          : piesaSingura(f.kind, f.w, f.h, { hNivel: plan.nivel })
            ?? piesaPlan(f.kind, f.w, f.h);
        if (!piesa) return null;
        const sel = (ales?.fel === "mobila" && ales.i === i)
          || !!mobilaAlese?.includes(i);
        // OGLINDIREA VINE DUPĂ ROTIRE, în sistemul piesei: `scale(-1, 1)` o
        // întoarce stânga-dreapta pe lungul ei, oricum ar fi ea așezată în
        // cameră. Cutia de apucat rămâne aceeași, fiindcă e simetrică.
        //
        // LITERELE NU SE ÎNTORC CU EA. Aici scria că desenul suportă scara
        // negativă «fiindcă nicio piesă din bibliotecă nu are text» — adevărat
        // când s-a scris, fals de pe 13.10.2026, când mașinile de spălat și-au
        // primit siglele «MSV» și «MSR». Piesa spune deci, printr-un context,
        // dacă e oglindită, iar sigla se întoarce înapoi (v. `Oglinda`).
        const ogl = f.oglX || f.oglY
          ? ` scale(${f.oglX ? -1 : 1} ${f.oglY ? -1 : 1})` : "";
        return (
          <g key={`f${i}`} data-fel={f.kind}
             transform={`translate(${f.c[0]} ${f.c[1]}) rotate(${f.deg})${ogl}`}>
            <Oglinda.Provider
              value={{ x: !!f.oglX, y: !!f.oglY, deg: f.deg ?? 0 }}>
              {piesa}
            </Oglinda.Provider>
            {/* Suprafața de apucat, invizibilă: desenul unei piese e din linii
                subțiri, iar un click pe golul dintre ele n-ar prinde nimic. */}
            <rect x={-f.w / 2} y={-f.h / 2} width={f.w} height={f.h}
                  fill="transparent"
                  stroke={sel ? SEL : "none"} strokeWidth={2}
                  strokeDasharray={sel ? "6 4" : undefined}
                  vectorEffect="non-scaling-stroke"
                  style={{ cursor: interactiv ? "move" : undefined }}
                  pointerEvents={interactiv ? "all" : "none"}
                  onPointerDown={apuca({ fel: "mobila", i })} />
            {/* PĂRȚILE SCĂRII SE APUCĂ SEPARAT. Peste scara desenată, fiecare
                rampă și fiecare podest are zona lui: apăsate, deschid panoul cu
                treptele, respectiv cu adâncimea podestului. Stau ÎNAINTEA
                dreptunghiului de mai jos, deci îl acoperă — altfel clicul ar
                alege scara întreagă, ca până acum. */}
            {f.scara && interactiv && (() => {
              const p = partileScarii(
                f.scara.rampe.map((r) => ({
                  de: [r.de[0] - f.c[0], r.de[1] - f.c[1]] as [number, number],
                  la: [r.la[0] - f.c[0], r.la[1] - f.c[1]] as [number, number],
                })), f.scara.latime_m, f.scara.podest_m,
                podesteleScarii(f.scara, adu));
              const zona = (pts: number[][], parte: "rampa" | "podest",
                            k: number) => (
                <path key={`${parte}${k}`} className="ob-scara-parte"
                      data-parte={parte} data-k={k}
                      d={`M${pts.map((q) => `${q[0]} ${q[1]}`).join("L")}Z`}
                      fill="transparent"
                      stroke={ales?.fel === "scaraParte" && ales.i === i
                              && ales.parte === parte && ales.k === k
                                ? SEL : "none"}
                      strokeWidth={2} strokeDasharray="6 4"
                      vectorEffect="non-scaling-stroke"
                      style={{ cursor: "pointer" }} pointerEvents="all"
                      onPointerDown={apuca({ fel: "scaraParte", i, parte, k })} />
              );
              return (
                <g>
                  {p.podeste.map((q, k) => zona(q, "podest", k))}
                  {p.rampe.map((q, k) => zona(q, "rampa", k))}
                </g>
              );
            })()}
          </g>
        );
      })}
    </g>
  );
}

/** APARTAMENTUL FIECĂRUI LIVING: câte camere are și cât e suprafața utilă.
 *
 *  Cerut scris pe planșă, în living (02.10.2026): «each apartment has its own
 *  living room... add in the living room the surface area and the apartment
 *  type». Livingul e locul firesc — e camera care dă numele apartamentului, și
 *  în tot catalogul fiecare apartament are exact unul.
 *
 *  A mai fost o dată aici și a fost scos (4a584939), fiindcă se scria ca AL
 *  TREILEA RÂND, sub aria camerei: două arii una sub alta, fără nimic care să
 *  spună care-i a cui. Se întoarce ca ANTET deasupra numelui, gri și mai mic —
 *  socoteala e aceeași, așezarea e alta.
 *
 *  CUM SE GRUPEAZĂ CAMERELE PE APARTAMENTE. `Camera.flat` e scris numai unde o
 *  latură ține mai multe apartamente (din tot catalogul, doar M5); unde latura
 *  are unul singur, e nescris peste tot. Deci: dacă pe latura asta scrie
 *  undeva, se grupează pe el ȘI se lasă afară camerele fără el — sunt spațiile
 *  comune ale laturii, care nu-s ale niciunui apartament. Dacă nu scrie
 *  nicăieri, toată latura e un apartament.
 *
 *  NUMĂRUL DE CAMERE SE NUMĂRĂ, nu se citește din `sides[].flats[].camere`:
 *  eticheta stă pe geometria desenată, care se schimbă sub mâna omului, iar
 *  cifra scrisă în temă ar rămâne în urmă la prima cameră adăugată.
 *
 *  SUPRAFAȚA UTILĂ nu cuprinde balcoanele și logiile — `afara` le spune —,
 *  fiindcă asta înseamnă «utilă». */
const CAMERE_DE_LOCUIT = new Set(["living", "bedroom", "dormitor", "room"]);

// FELURILE DE ZONĂ, roșul lor și grosimea stau în `plan/arhMaterial`, nu
// aici: conturul unei camere se trage din DOUĂ locuri — planșa tronsonului
// și planul de nivel —, iar regula trebuie să fie una. Pusă numai aici, zona
// ieșea roșie în editor și neagră pe planul de nivel, adică tocmai pe
// ecranul pe care se uita arhitectul («it's still not there. WHY»,
// 15.10.2026).

export function apartamentulLivingului(
  camere: readonly { kind: string; side: string; flat?: number;
                     arie: number | null; arieCladita?: number | null;
                     afara: unknown }[],
): Map<number, { camere: number; utila: number; cladita: number }> {
  // Laturile care chiar au apartamente numerotate.
  const numerotata = new Set<string>();
  for (const c of camere) if (c.flat != null) numerotata.add(c.side);

  const cheia = (c: { side: string; flat?: number }) =>
    (numerotata.has(c.side)
      ? (c.flat == null ? null : `${c.side}#${c.flat}`)
      : `${c.side}#0`);

  const grup = new Map<string, { camere: number; utila: number; cladita: number }>();
  for (const c of camere) {
    const k = cheia(c);
    if (k === null) continue;
    const g = grup.get(k) ?? { camere: 0, utila: 0, cladita: 0 };
    if (CAMERE_DE_LOCUIT.has(c.kind)) g.camere++;
    // ARIA CONSTRUITĂ A APARTAMENTULUI, adunată pe aceeași regulă ca cea utilă:
    // numai camerele DINĂUNTRU. Terasa și balconul se sar — `afara` le prinde
    // pe amândouă —, cerut anume: «Aria construită (fara terasa...)».
    //
    // JUMĂTATEA PEREȚILOR COMUNI VINE DE-A GATA: `arieCladita` a fiecărei
    // camere o cuprinde deja (v. `arieConstruita.test`), iar adunarea lor o
    // duce mai departe fără s-o socotească a doua oară — «cum face acum sa ia
    // jumatatea peretilor comuni dintre apartamente unde avem apartamente
    // conectate».
    if (!c.afara) {
      g.utila += c.arie ?? 0;
      g.cladita += c.arieCladita ?? c.arie ?? 0;
    }
    grup.set(k, g);
  }

  const out = new Map<number, { camere: number; utila: number; cladita: number }>();
  camere.forEach((c, i) => {
    if (c.kind !== "living") return;
    const k = cheia(c);
    const g = k === null ? null : grup.get(k);
    if (g) {
      out.set(i, { camere: g.camere,
                   utila: Math.round(g.utila * 100) / 100,
                   cladita: Math.round(g.cladita * 100) / 100 });
    }
  });
  return out;
}

/** ETICHETELE CAMERELOR — nume, arie, corp, loc —, socotite pe geometrie și
 *  mărire. Sunt și ce apără cotele de suprapunere: fiecare etichetă vizibilă
 *  își ocupă dreptunghiul (v. `cutiaEtichetei`). `null` pe camera în care
 *  numele nu încape.
 *
 *  Exportată fiindcă planul unui nivel de arhitectură își scrie camerele cu
 *  ea (v. `plan/AdnotariNivel`): o cameră se numește la fel pe tronson și pe
 *  nivel, fiindcă e același cod. */
/** CADRUL ÎNSCRIS AL UNEI CAMERE, ținut minte între măriri.
 *
 *  `etichetelePlanului` se reface la FIECARE pas de zoom — corpul textului e
 *  în pixeli, deci așezarea chiar depinde de mărire. Dar cadrul înscris NU
 *  depinde: el e geometria camerei, iar `cadru` o caută rotind poligonul pe
 *  fiecare latură a lui. Pe un modul cu multe camere asta se plătea de zeci
 *  de ori pe secundă cât ții degetul pe rotiță — «I'm having a lag on 3 cam
 *  luxury because there are a lot of areas that are being calculated when we
 *  zoom in/out. We should fix this by maybe saving them between zooms»
 *  (19.10.2026).
 *
 *  CHEIA E CHIAR INELUL, într-un `WeakMap`: `deriva` construiește tablouri
 *  noi numai când se schimbă documentul, deci o panoramare sau o mărire
 *  nimerește cache-ul, iar o editare îl ratează — exact ce trebuie, fără
 *  nicio invalidare scrisă de mână. Și nu ține nimic în viață: intrarea
 *  pleacă odată cu inelul.
 *
 *  NUMAI CADRUL. Așezarea textului rămâne recalculată, fiindcă ea chiar se
 *  schimbă cu mărirea; aici se taie doar ce nu se schimbă. */
const cadreCache = new WeakMap<object, ReturnType<typeof cadru>>();
/** Câte cadre s-au CALCULAT de la pornire — numai pentru probă (v.
 *  `zoomCache.test`). Un contor, nu o listă: cache-ul e un `WeakMap` și n-are
 *  cum să fie numărat din afară. */
export let cadreCalculate = 0;
const cadruCamerei_ = (inel: readonly Pt[]) => {
  const cheie = inel as unknown as object;
  const gata = cadreCache.get(cheie);
  if (gata !== undefined) return gata;
  const nou = cadru(inel as unknown as number[][]);
  cadreCalculate++;
  cadreCache.set(cheie, nou);
  return nou;
};

export function etichetelePlanului(plan: PlanTronson, pxPerM: number,
                                   mod: "plan" | "design" = "plan") {
  const mobila = plan.piese.map(cutiaPiesei);
  const apart = apartamentulLivingului(plan.camere);
  return topesteEtichetele(plan.camere, pxPerM, plan.camere.map((c, idxCamera) => {
    // CADRUL DINĂUNTRU, nu cutia din jur. Pe o cameră în L — holurile de
    // distribuție sunt mai toate așa — mijlocul cutiei cade în scobitură,
    // adică în camera de alături, și acolo ajungea și scrisul. Cadrul
    // înscris dă un mijloc care e sigur în cameră, și pe deasupra spune
    // și cât loc are textul până în pereți.
    const cad = cadruCamerei_(c.inel);
    const orizontal = cad ? Math.abs(Math.cos((cad.deg * Math.PI) / 180)) > 0.5 : true;
    const locX = cad ? (orizontal ? cad.w : cad.h) : 0;
    const locY = cad ? (orizontal ? cad.h : cad.w) : 0;

    // NUMELE SCRIS BATE FELUL. O cameră poate purta cuvântul omului («Cramă»),
    // iar felul canonic de dedesubt rămâne al motoarelor — v. `CameraDoc.label`.
    // Pe planșă se scrie ce ar scrie arhitectul, nu eticheta din vocabular.
    const numeLung = numeleCamerei(c.kind, c.eticheta).toUpperCase();
    // …și numele scurt, dacă felul are unul: v. mai jos, unde se alege.
    const numeScurt = (numeScurtCamerei(c.kind) ?? "").toUpperCase();
    const arie = c.arie != null ? `${c.arie.toFixed(2)} m²` : "";
    const ap = apart.get(idxCamera);
    // ARIA CONSTRUITĂ, O SINGURĂ DATĂ PE APARTAMENT, nu pe fiecare cameră.
    //
    // Era scrisă sub aria utilă a FIECĂREI camere — cerută așa pe 13.10.2026.
    // Arhitectul a schimbat cererea pe 19.10: «Aria construită (fara terasa si
    // cum face acum sa ia jumatatea peretilor comuni dintre apartamente unde
    // avem apartamente conectate) should be right under this text and it
    // should be per apartment (just in the living room like in one spot) not
    // per room». Zece camere cu zece «Ac» nu spun nimic despre apartament:
    // cifra care se caută e a LUI, iar ca s-o afli trebuia să le aduni cu
    // ochiul.
    //
    // ÎN LIVING, fiindcă acolo stă și antetul apartamentului (v. `ap`): e
    // singurul loc în care cifrele apartamentului sunt deja împreună. În
    // restul camerelor rândul rămâne gol.
    //
    // SCRISĂ CU «Ac», aria utilă rămâne fără nimic: așa se scriu pe planșe, iar
    // cifra de deasupra e cea pe care o caută toată lumea. Două cifre una sub
    // alta, amândouă fără nume, n-ar spune care e care.
    const cladita = ap && ap.cladita > ap.utila + 0.005
      ? `Ac ${ap.cladita.toFixed(2)} m²` : "";
    // Lățimea unui text, în metri. Majusculele unei sans cu spațiere stau la
    // cam 0,68 din corp pe literă; cifrele la 0,55.
    const lat = (t: string, corp: number, f: number) => t.length * corp * f;

    // CORPUL E ÎN PIXELI, ca al cotelor, și cu două puncte peste ele: la
    // orice mărire numele se citește primul, strâns doar când camera e prea
    // mică pentru el. În metri ar fi de două ori cotele la mărirea de lucru
    // și abia citibil la cea de ansamblu.
    let h: number;
    // DOUĂ RÂNDURI: numele și, sub el, aria — așezarea de pe planșa de
    // referință. Se micșorează cât să încapă pe lățime; dacă pe înălțime nu
    // intră două rânduri, rămâne numele singur — o cameră fără nume nu se
    // recunoaște, una fără arie se măsoară de pe cote.
    // ANTETUL APARTAMENTULUI, numai în living (v. `apartamentulLivingului`).
    // Nu intră în micșorarea de mai jos: numele și aria camerei își păstrează
    // corpul, iar antetul se strânge singur cât îi trebuie ca să încapă — altfel
    // un rând de notă ar fi tras în jos numele, care e ce se citește primul.
    // DOUĂ SCRIERI, UNA LUNGĂ ȘI UNA SCURTĂ. «AP. 2 CAMERE · 66,77 m² UTILI» nu
    // încape într-o garsonieră: strâns cât să intre, textul scade sub citibil și
    // atunci antetul dispărea cu totul — adică tocmai apartamentele mici
    // rămâneau fără etichetă. Se încearcă forma lungă; dacă nu intră la un corp
    // citibil, se scrie cea scurtă, «AP. 1 CAM · 43,86 m²».
    const antetLung = ap
      ? tn("eticheta.apartament#n", ap.camere, { s: ap.utila.toFixed(2) })
      : "";
    const antetScurt = ap
      ? tn("eticheta.apartament_scurt#n", ap.camere, { s: ap.utila.toFixed(2) })
      : "";

    // CORPUL LA CARE ÎNCAPE UN NUME DAT. Scos ca funcție fiindcă se întreabă de
    // două ori: o dată pentru numele întreg, o dată pentru cel scurt.
    const corpPentru = (n: string) => {
      let hh = Math.min(ETICHETA_PX / pxPerM, c.mic / 4.6);
      const cere = Math.max(lat(n, hh, 0.68), lat(arie, hh * ARIE_RAPORT, 0.55),
                            lat(cladita, hh * ARIE_RAPORT, 0.55));
      if (cere > locX * 0.9) hh *= (locX * 0.9) / cere;
      return hh;
    };
    // NUMELE SCURT SALVEAZĂ ETICHETA, nu o scurtează din gust. «ZONĂ DE
    // DESFUMARE» într-un colț de 0,7 m² se strânge sub pragul de citit, deci
    // camera rămânea FĂRĂ NICIO etichetă — iar o zonă nescrisă pe planșă e o
    // zonă pe care n-o vede nimeni. Se încearcă întâi numele întreg; numai
    // dacă el pică sub prag se ia cel scurt, și numai dacă ăla chiar ajută.
    // Aceeași alegere ca la antetul apartamentului, de mai jos.
    h = corpPentru(numeLung);
    let nume = numeLung;
    if (numeScurt && h * pxPerM < PRAG_NUME_PX) {
      const hScurt = corpPentru(numeScurt);
      if (hScurt > h) { nume = numeScurt; h = hScurt; }
    }
    let randuri = arie ? 2 : 1;
    if (randuri === 2 && h * 2.2 > locY * 0.9) randuri = 1;
    // Aria, de la 6 px în sus: la mărirea la care se vede tot modulul cifra e
    // mică, dar e chiar cifra pentru care se deschide planul.
    const cuArie = randuri === 2 && h * ARIE_RAPORT * pxPerM >= 6;
    // A TREIA CIFRĂ INTRĂ NUMAI DACĂ ÎNCAPE ÎNTREAGĂ. Pe o baie de 4 m² abia
    // intră numele și aria utilă; aria construită n-are voie să le strângă pe
    // ele ca să intre ea — se lasă afară, și cine o vrea o citește din panou.
    const cuCladita = cuArie && !!cladita
      && h * (2.1 + ARIE_RAPORT * 1.15) <= locY * 0.9;
    if (h * pxPerM < PRAG_NUME_PX) return null;

    // CÂT DE MARE E ANTETUL: pornește de la raportul lui și se strânge până
    // intră pe lățime. Se scrie dacă rămâne citibil (5 px) și dacă mai e loc pe
    // înălțime deasupra numelui. Pe garsoniera lui M2 — living de 15 m² —
    // tocmai strângerea asta îl face să încapă, deci fiecare apartament își
    // poartă eticheta, nu doar cele mari.
    // LĂȚIMEA ANTETULUI SE SOCOTEȘTE CA MAJUSCULE RĂSFIRATE, nu ca cifre.
    // Prima oară a fost socotit cu factorul ariei (0,55 — potrivit pentru
    // cifre), deși textul e cu MAJUSCULE și mai are și răsfirare: ieșea cu un
    // sfert mai lat decât credea potrivirea și trecea prin peretele livingului
    // în bucătărie (văzut la randare, înainte de a ajunge pe ecranul nimănui).
    // 0,68 e factorul numelui de cameră, tot majuscule; răsfirarea se adună
    // peste el, fiindcă se pune după fiecare literă.
    // 0,86 din lățimea liberă, nu toată: antetul e cel mai lung rând al
    // etichetei, iar lățimea lui e ESTIMATĂ (litere × corp × factor), nu
    // măsurată. Fără marginea asta ieșea prin peretele livingului în bucătărie.
    const incape = (t: string) => {
      let hh = h * ANTET_RAPORT;
      const cere2 = lat(t, hh, FACTOR_ANTET);
      if (cere2 > locX * 0.86) hh *= (locX * 0.86) / cere2;
      return hh;
    };
    let antet = antetLung;
    let hAntet = incape(antetLung);
    if (antetScurt && hAntet * pxPerM < ANTET_MIN_PX) {
      const hScurt = incape(antetScurt);
      if (hScurt > hAntet) { antet = antetScurt; hAntet = hScurt; }
    }
    const cuAntet = !!antet && hAntet * pxPerM >= ANTET_MIN_PX
      && h * (cuArie ? 2.1 : 1.2) + hAntet * 1.9 <= locY * 0.95;

    // ÎN DESIGN, ETICHETA SE DĂ LA O PARTE DE PLAFONIERĂ: amândouă cad în
    // mijlocul camerei. Se mută TEXTUL, nu simbolul — poziția corpului e o
    // decizie scrisă în document, eticheta e adnotare.
    const dus = mod === "design" ? Math.min(0.62, c.mic * 0.26) : 0;
    const latime = Math.max(lat(nume, h, 0.68),
                            cuArie ? lat(arie, h * ARIE_RAPORT, 0.55) : 0,
                            cuCladita ? lat(cladita, h * ARIE_RAPORT, 0.55) : 0,
                            cuAntet ? lat(antet, hAntet, FACTOR_ANTET) : 0);
    // LOCUL, OCOLIND MOBILA. Mijlocul cadrului e locul firesc, dar în camera
    // mobilată acolo stă patul sau masa, iar numele scris peste ele se citește
    // pe jumătate. Se încearcă mijlocul, apoi locuri tot mai depărtate de el
    // în cadrul camerei — 49 de candidați pe o grilă —, și se ia primul a
    // cărui cutie stă în cameră și nu atinge nicio piesă. Fără niciunul liber,
    // rămâne mijlocul: o cameră fără nume e mai rău decât un nume peste o
    // canapea.
    const inaltime = h * (cuArie ? 2.1 : 1.2)
      + (cuCladita ? h * ARIE_RAPORT * 1.15 : 0)
      + (cuAntet ? hAntet * 1.9 : 0);
    const [lx, ly] = locLiber(cad, c.inel, latime, inaltime, mobila);
    // Blocul rămâne centrat pe locul găsit: numele coboară cu jumătate din cât
    // a adăugat antetul deasupra lui, și urcă cu jumătate din cât adaugă aria
    // dedesubt.
    const cy = ly + dus - (cuArie ? h * 0.55 : 0)
      - (cuCladita ? h * ARIE_RAPORT * 0.58 : 0)
      + (cuAntet ? hAntet * 0.95 : 0);
    const cx = lx;
    // HALOU DE HÂRTIE sub literă NUMAI ÎN DESIGN, unde textul cade peste
    // mobilier colorat și peste trasee. Pe planșă nu: rostul e gri deschis,
    // iar conturul alb în jurul literelor era el însuși o pată («fără contur
    // alb»).
    const halou = mod === "design"
      ? { stroke: HARTIE, strokeWidth: h * 0.34,
          paintOrder: "stroke" as const, strokeLinejoin: "round" as const }
      : {};
    return { cx, cy, h, nume, arie, cuArie, cladita, cuCladita,
             // cifrele și ca NUMERE, ca două etichete topite să le poată aduna
             arieNum: c.arie, claditaNum: c.arieCladita,
             /** Camerele topite în eticheta asta — v. `topesteEtichetele`. */
             topite: undefined as number[] | undefined,
             antet, cuAntet, hAntet, halou,
             cutie: cutiaEtichetei(
               cx, cy + (cuArie ? h * 0.55 : 0)
                 + (cuCladita ? h * ARIE_RAPORT * 0.58 : 0)
                 - (cuAntet ? hAntet * 0.95 : 0),
               h, latime,
               (cuArie ? 2 : 1) + (cuCladita ? 1 : 0) + (cuAntet ? 1 : 0)) };
  }));
}

/** Se ating două dreptunghiuri de scris? */
const seCalca = (a: Cutie, b: Cutie) =>
  a.x0 < b.x1 && b.x0 < a.x1 && a.y0 < b.y1 && b.y0 < a.y1;

/** ÎMPART DOUĂ CAMERE ACELAȘI SPAȚIU? Exportată ca să poată fi probată: pe
 *  planurile de azi etichetele se ating numai când camerele se suprapun, deci
 *  regula nu se poate deosebi mergând prin desen. */
export function cameraPesteCamera(a: readonly Pt[], b: readonly Pt[]): boolean {
  if (a.length < 3 || b.length < 3) return false;
  // destul: dacă MIJLOCUL uneia cade în cealaltă, stau în același spațiu
  const mij = (r: readonly Pt[]): Pt => {
    const xs = r.map((q) => q[0]), ys = r.map((q) => q[1]);
    return [(Math.min(...xs) + Math.max(...xs)) / 2,
            (Math.min(...ys) + Math.max(...ys)) / 2];
  };
  return inInel(mij(a), b) || inInel(mij(b), a);
}

/** DOUĂ NUME PESTE ACELAȘI LOC SE SCRIU ÎNTR-UNUL: «CAMERA DE ZI +
 *  BUCĂTĂRIE», cu ariile adunate.
 *
 *  Cerut cu exemplul cu tot (14.10.2026): «in the case where we have living
 *  room + kitchen we should just say "Camera de zi + Bucatarie" if it's in the
 *  same room, and not let the writing overlap».
 *
 *  SE TOPESC NUMAI CAMERELE CARE CHIAR ÎMPART UN SPAȚIU, nu oricare două
 *  etichete care se ating. Prima încercare lua orice atingere de dreptunghiuri
 *  și scria «CAMERA DE ZI + BALCON» — un living și balconul lui nu sunt un
 *  spațiu, sunt înăuntru și afară —, iar textul topit, pus la mijlocul lor,
 *  ieșea 160 cm afară din living. Deci două condiții: să fie amândouă
 *  înăuntru (sau amândouă afară), și una să stea CHIAR PESTE cealaltă.
 *
 *  SCRISUL RĂMÂNE UNDE ERA cel mai mare dintre ele — un loc despre care se
 *  știe deja că e bun (v. `locLiber`) — și se STRÂNGE cât să ocupe aceeași
 *  lățime: numele topit e mai lung, iar lăsat la corpul vechi ar ieși din
 *  cameră tocmai pe acolo pe unde intrase. */
function topesteEtichetele<T extends {
  cx: number; cy: number; h: number; nume: string; cutie: Cutie;
  arie: string; cuArie: boolean; arieNum: number | null;
  cladita: string; cuCladita: boolean; claditaNum: number | null;
  topite?: number[];
} | null>(
  camere: readonly { inel: Pt[]; afara: unknown }[], pxPerM: number, et: T[],
): T[] {
  const out = [...et];
  for (let i = 0; i < out.length; i++) {
    const a = out[i];
    if (!a || a.nume.includes(" + ")) continue;
    for (let j = i + 1; j < out.length; j++) {
      const b = out[j];
      if (!b || !seCalca(a.cutie, b.cutie)) continue;
      const ca = camere[i], cb = camere[j];
      if (!ca || !cb) continue;
      // înăuntru cu înăuntru, afară cu afară
      if (!ca.afara !== !cb.afara) continue;
      // …și chiar în același spațiu, nu doar vecine
      if (!cameraPesteCamera(ca.inel, cb.inel)) continue;

      const nume = `${a.nume} + ${b.nume}`;
      // corpul, strâns cât să ocupe aceeași lățime ca numele de dinainte
      const h = a.h * (a.nume.length / nume.length);
      if (h * pxPerM < PRAG_NUME_PX) continue;   // topit, n-ar mai fi citibil
      const arie = (a.arieNum ?? 0) + (b.arieNum ?? 0);
      const cladita = (a.claditaNum ?? 0) + (b.claditaNum ?? 0);
      out[i] = { ...a, nume, h,
        // CE S-A TOPIT, pe indicii camerelor. Nu e pentru desen: e singurul fel
        // în care se poate întreba, pe catalog, dacă topirea a legat lucruri
        // care chiar se leagă — două camere ale aceluiași spațiu, amândouă
        // înăuntru. Fără el, o topire greșită se vede doar ca un nume ciudat
        // pe o planșă pe care nu se uită nimeni.
        topite: [...(a.topite ?? [i]), j],
        arie: arie > 0 ? `${arie.toFixed(2)} m²` : a.arie,
        arieNum: arie,
        cladita: cladita > arie + 0.005 ? `Ac ${cladita.toFixed(2)} m²` : "",
        cuCladita: a.cuCladita && cladita > arie + 0.005,
        claditaNum: cladita,
      } as T;
      out[j] = null as T;
    }
  }
  return out;
}

export type EtichetaCamerei = NonNullable<ReturnType<typeof etichetelePlanului>[number]>;

/** NUMELE ȘI ARIA CAMEREI, cu majuscule, aria dedesubt mai mică, amândouă cu
 *  tușul planșei. `tus` îl schimbă pe planșa de noapte a nivelului, unde
 *  hârtia e neagră. */
export function NumeleCamerelor({ etichete, tus }: {
  etichete: readonly (EtichetaCamerei | null)[];
  tus?: string;
}) {
  return (
    <>
      {etichete.map((e, i) => e && e.cuAntet && (
        // APARTAMENTUL, DEASUPRA NUMELUI CAMEREI, în <text> PROPRIU.
        //
        // Nu ca `tspan` în textul numelui, deși acolo i-ar fi locul firesc:
        // proba care păzește etichetele de ieșirea din cameră
        // (`eticheta.test.tsx`) citește rândurile din acel `<text>` și
        // socotește PRIMUL rând ca nume de cameră, cu corpul etichetei. Antetul
        // strecurat în față trecea drept nume scris mare, iar măsurătoarea ieșea
        // de câteva ori mai lată decât scrisul adevărat. Despărțit, numele
        // rămâne primul rând al blocului lui, iar antetul își are paza lui
        // (`apartamentul.test.ts`).
        //
        // TUȘUL E AL PLANȘEI, doar mai străveziu: planșa are trei cerneluri
        // (v. `doc.test.ts`), iar un gri nou ar fi fost a patra. Străveziul dă
        // aceeași cenușie fără să adauge o culoare.
        <text key={`ap${i}`} x={e.cx} y={e.cy - e.hAntet * 1.9}
              textAnchor="middle" dominantBaseline="middle"
              fontSize={e.hAntet} fontWeight={600} fill={tus ?? ROOM_NAME}
              /* ANTETUL NU MAI E STINS. Stătea la jumătate de străveziu, și
                 el e singurul scris de pe planșă care chiar ieșea gri
                 deschis — iar pe el stă cifra cerută negru («make that text
                 black not light gray», 13.10.2026). Rămâne un ANTET prin ce
                 e: mai mic și răsfirat, nu prin faptul că se stinge. Pe
                 planșa de noapte îi rămâne străveziul lui, unde cerneala e
                 albă pe negru și plinul ar fi orbitor. */
              fillOpacity={tus ? 0.75 : undefined}
              letterSpacing={e.hAntet * ANTET_RASFIRARE} {...e.halou}>
          {e.antet}
        </text>
      ))}
      {etichete.map((e, i) => e && (
        <text key={i} x={e.cx} y={e.cy}
              textAnchor="middle" dominantBaseline="middle"
              fontSize={e.h} fontWeight={700} fill={tus ?? ROOM_NAME}
              letterSpacing={e.h * 0.06} {...e.halou}>
          {e.nume}
          {e.cuArie && (
            <tspan x={e.cx} dy={e.h * 1.15} fill={tus ?? AREA_INK}
                   fontWeight={ARIE_GROSIME} fontSize={e.h * ARIE_RAPORT}
                   letterSpacing={0}>
              {e.arie}
            </tspan>
          )}
          {e.cuCladita && (
            <tspan x={e.cx} dy={e.h * ARIE_RAPORT * 1.15} fill={tus ?? AREA_INK}
                   fontWeight={ARIE_GROSIME} fontSize={e.h * ARIE_RAPORT}
                   letterSpacing={0}>
              {e.cladita}
            </tspan>
          )}
        </text>
      ))}
    </>
  );
}

/** CÂT STĂ MÂNA PE LOC ÎNAINTE SĂ SE REAȘEZE ADNOTAREA.
 *
 *  Puțin sub cât ține o așezare pe planșa mare (~130 ms, v. `useMarireAsezata`):
 *  destul cât o baleiere de rotiță să nu ceară niciuna pe drum, și prea puțin
 *  ca ochiul să prindă întârzierea după ce s-a oprit.
 */
const RAGAZ_ADNOTARE_MS = 120;

/** MĂRIREA LA CARE S-A AȘEZAT ADNOTAREA ULTIMA OARĂ — poate rămâne în urma
 *  celei cu care se DESENEAZĂ, și e singura care are voie.
 *
 *  Așezarea cotelor caută, pentru fiecare față, cea mai bună treaptă de
 *  depărtare și cel mai bun loc al cifrei pe linie (v. `cote::cotelePlanului`,
 *  `asezarea`). Căutarea ține de mărire din rădăcină — depărtările, corpul
 *  cifrei și pragurile sunt în PIXELI —, deci nu se poate scoate pe dinafara
 *  măririi, nici memorată pe plan: la altă mărire e alt răspuns.
 *
 *  CÂT COSTĂ, măsurat pe «23 IZOLAT LUXURY» (139 camere, 215 ziduri, 403
 *  piese): ~130 ms la fiecare schimbare de mărire, din care 60-75 numai
 *  căutarea așezării. Restul socotelii — piedicile, perechile de fețe, conturul
 *  fără colineare — ține sub 10 ms la un loc, deci n-avea ce să salveze o
 *  despărțire a ei în parte care ține de mărire și parte care nu. Și se
 *  plătește o dată pe FIECARE corp de pe masă (v. `editor/Panza`).
 *
 *  O rotiță de trackpad dă zeci de măriri pe secundă, iar fiecare cerea
 *  socoteala întreagă — de-aici lag-ul arătat de Bianca (21.10.2026): «a lot of
 *  areas that are being calculated when we zoom in/out».
 *
 *  SE AȘTEAPTĂ MÂNA, nu se amână randarea. Cât se învârte rotița nu se socotește
 *  NIMIC: desenul se mișcă singur, cu adnotarea de dinainte, iar așezarea se
 *  face o dată, la oprire, cu mărirea din urmă. O baleiere de 60 de pași cere o
 *  așezare în loc de 61 (numărate, v. `adnotareAsezata.test.tsx`), iar o
 *  schimbare de mărire scade de la 175 la 67 ms — aproape de cele 45 pe care
 *  le-ar costa stinsă de tot, adică de randarea desenului însuși.
 *
 *  `useDeferredValue` NU FACE ASTA, și am încercat-o întâi: ea dă randarea
 *  scumpă mai încolo, dar tot o dă — React apucă s-o înceapă în golul dintre
 *  două rotițe, iar o dată începută nu mai poate fi întreruptă. Numărate, tot
 *  60 de așezări pe baleiere; ce se câștiga era doar ordinea lor față de
 *  desen. Ragazul le TAIE, nu le mută.
 *
 *  LA CAPĂTUL ORICĂREI MĂRIRI REZULTATUL E EXACT CEL DE PÂNĂ ACUM: se
 *  rotunjește CÂND se socotește, nu la CE mărire — de-aia nu e o treaptă de
 *  mărire, care ar fi așezat cotele puțin altfel decât o cere planșa.
 *
 *  CE SE VEDE CÂT SE ÎNVÂRTE ROTIȚA: numele și cifrele cresc odată cu desenul,
 *  în loc să-și țină corpul în pixeli, și se așază la loc când mâna se oprește.
 *
 *  PRIMA MĂRIRE INTRĂ PE LOC, fără ragaz: o planșă deschisă vine cotată din
 *  prima, nu după o clipă de goliciune. */
/** …ȘI TOT AȘA PENTRU PLANUL CARE SE SCHIMBĂ SUB MÂNĂ.
 *
 *  Un perete tras dă un document nou la fiecare `pointermove`, exact ca rotița,
 *  iar așezarea se refăcea de fiecare dată. Măsurat pe «23 IZOLAT LUXURY», un
 *  pas de tragere ținea 373 ms: 228 `deriva`, ~97 adnotarea, ~48 randarea
 *  propriu-zisă. `deriva` s-a ieftinit de cinci ori (v. `cutiaInel`), iar
 *  adnotarea e ce a rămas din jumătatea mare.
 *
 *  CE SE VEDE CÂT SE TRAGE: liniile de cotă ale planșei rămân în urmă cu cel
 *  mult un ragaz și se așază la loc când mâna se oprește. Zidurile, camerele,
 *  mobila și hașurile merg mai departe LIVE — ele sunt desenul însuși.
 *
 *  MĂSURA OBIECTULUI TRAS NU ÎNTÂRZIE, și de-aia se poate face asta: ea nu
 *  vine de aici, ci din `editor/Panza`, din `coteleObiectului(plan, ales)` pe
 *  planul VIU. Cerința din 21.10.2026 — «atunci când vreau să măresc sau să
 *  micșorez o fereastră mi-ar plăcea să văd și dimensiunea ei în timp real» —
 *  rămâne întreagă.
 *
 *  `ales` RĂMÂNE ÎN AFARA RAGAZULUI (v. memo-ul `cote`): o schimbare de
 *  selecție e un fapt singur, nu un șuvoi, iar cotele ei trebuie să apară la
 *  apăsare, nu peste o zecime de secundă. */
function useAsezat<T>(px: number, plan: T): { px: number; plan: T } {
  const [asezat, setAsezat] = useState({ px, plan });
  // Valorile din urmă, citite de ceas fără să-l rearmeze: ce se așază la oprire
  // e UNDE A AJUNS mâna, nu de unde a pornit ragazul.
  const acum = useRef({ px, plan });
  acum.current = { px, plan };
  useEffect(() => {
    if (px === asezat.px && plan === asezat.plan) return undefined;
    const ceas = setTimeout(() => setAsezat(acum.current), RAGAZ_ADNOTARE_MS);
    // Rearmarea: orice schimbare nouă stinge ceasul de dinainte, deci cât ține
    // baleierea — ori tragerea — nu se socotește nimic.
    return () => clearTimeout(ceas);
  }, [px, plan, asezat]);
  return asezat;
}

/** A plan figure's click circle: 13 px, cut at half its cote, and stopped at
 *  every device's grab circle. The figures are drawn above the devices, so at
 *  fit zoom a socket standing under one could not be clicked at all. */
function razaCifrei(c: Cota, pxPerM: number,
                    prinse: readonly { c: Pt; r: number }[]): number {
  const x = c.a[0] + (c.b[0] - c.a[0]) * (c.la ?? 0.5);
  const y = c.a[1] + (c.b[1] - c.a[1]) * (c.la ?? 0.5);
  return Math.min(13 / pxPerM,
                  Math.hypot(c.b[0] - c.a[0], c.b[1] - c.a[1]) / 2,
                  ...prinse.map((p) =>
                    Math.max(0, Math.hypot(x - p.c[0], y - p.c[1]) - p.r)));
}

export default function Desen({
  plan, ales, alese, pereteleAlese, mobilaAlese, golurileAlese, laAles,
  pxPerM, interactiv = true,
  cursorPerete = "pointer", cuMobilier = true, mod = "plan", trasee,
  pensula = false, panorameaza = false,
  araEtichete = true, araCote = true, araPardoseli = true, culoriApartamente = null,
  electricsOpacity = 1,
  laLanturi,
  laCota,
}: {
  plan: PlanTronson;
  ales: Ales;
  /** CAMERELE ALESE, toate — cea din `ales` și cele adăugate cu ⌘.
   *
   *  `ales` rămâne camera DESPRE CARE se citește panoul și pe care stau
   *  apucătoarele; lista asta e pentru gesturile care cer mai multe deodată,
   *  unirea în primul rând. Fondul albastru le arată pe toate, deci selecția se
   *  vede și când ea nu mai e un singur lucru. */
  alese?: readonly number[];
  /** Pereții aleși, toți — se aprind ca și cel curent (v. `Masa::alege`). */
  pereteleAlese?: readonly number[];
  /** Piesele de mobilier adunate cu Shift — toate se aprind, nu doar cea al
   *  cărei panou se citește. Fără asta, selecția multiplă nu se VEDE, iar
   *  «Unește elemente» pare că lucrează pe ce nimerește. */
  mobilaAlese?: readonly number[];
  /** GOLURILE adunate cu ⇧ — se aprind toate, ca pereții și piesele. Un gol se
   *  numește `(fel2, i)`, fiindcă `i` e numărul lui în lista LUI: ușa 3 și
   *  fereastra 3 sunt lucruri deosebite. */
  golurileAlese?: readonly { fel2: "doors" | "windows"; i: number }[];
  laAles?: (a: Ales, e: React.PointerEvent) => void;
  /** «plan» e planșa tehnică: hârtie, tuș, camere albe. «design» e aceeași
   *  geometrie văzută ca AMENAJARE — pardoseala fiecărei camere desenată cu
   *  finisajul ei, instalația electrică peste ea. Nu sunt două desene, e același
   *  desen cu alte straturi aprinse: un perete mutat în design se mută și pe
   *  planșă, fiindcă e același perete. */
  mod?: "plan" | "design";
  /** Traseele firelor, gata calculate. Vin de sus fiindcă sunt scumpe (un
   *  Dijkstra pe graful pereților) și n-au de ce să se refacă la fiecare
   *  mișcare de mouse. */
  trasee?: readonly Traseu[];
  /** PENSULA E ACTIVĂ, deci clickul e al CAMEREI, orice ar fi deasupra ei.
   *
   *  Fără steagul ăsta, o cameră nu se putea vopsi decât pe porțiunile goale:
   *  plafoniera stă chiar în mijlocul ei, iar zona ei de apucat înghițea exact
   *  clickul cu care cineva ar fi vopsit-o — apăsai pe cameră și ți se selecta
   *  becul. La fel mobila, zidurile și golurile. Cât ține pensula, toate tac și
   *  clickul cade pe camera de dedesubt. */
  pensula?: boolean;
  /** SE PANORAMEAZĂ ACUM, deci desenul nu apucă nimic.
   *
   *  Panoramarea pornește din `onPointerDown`-ul pânzei, iar desenul stă peste
   *  ea și oprea propagarea la orice apăsare. Pe un modul plin nu mai rămâne
   *  fond liber de nicăieri, deci apăsarea cu spațiul ținut cădea pe un zid și
   *  nu ajungea la pânză: pânza nu se putea muta decât de pe puținul gol dintre
   *  camere. Butoanele mijloc și dreapta se rezolvă singure (v. `apuca`), dar
   *  spațiul e o stare a tastaturii pe care numai gazda o știe. */
  panorameaza?: boolean;
  /** SE SCRIU NUMELE ȘI ARIILE camerelor?
   *
   *  Pe un modul mic sunt reperul după care te uiți; pe unul plin, cu mobilă și
   *  instalație dedesubt, sunt încă un strat peste un desen deja încărcat. Cine
   *  desenează vrea să le poată da la o parte, ca pe orice adnotare. Cotele de
   *  pe laturi NU se sting odată cu ele: alea sunt măsuri, nu etichete. */
  araEtichete?: boolean;
  /** SE SCRIU COTELE pe fețele zidurilor? V. `cote.ts` pentru ce se scrie și
   *  ce tace. Implicit da: sunt măsuri, nu adnotări. */
  araCote?: boolean;
  /** Clic pe cifra unei cote a planșei — numai editorul o dă. Lipsă = desenul
   *  e de privit, nu de scris în el. */
  laCota?: (c: Cota, pastreazaSelectia?: boolean) => void;
  /** SPĂLĂTURA APARTAMENTELOR, pe rangul apartamentului (`Camera.flat`) —
   *  numai pe nivelul unei soluții, unde camerele știu al cui apartament sunt.
   *  `null` = stinsă. Stă peste hârtie și sub rostul pardoselii, ca pe desenul
   *  nivelului (v. `StoreyView::culoareaApartamentului`). */
  culoriApartamente?: readonly (string | undefined)[] | null;
  /** SE DESENEAZĂ PARDOSEALA CAMERELOR — fondul ei și rostul ei?
   *
   *  Pardoseala e cel mai des strat de pe planșă: acoperă fiecare centimetru
   *  de cameră, sub tot ce se desenează peste ea. Pe planșă e rostul în gri
   *  (scânduri, dale, grăunte), în Design e fondul colorat al finisajului — și
   *  în amândouă vine un moment în care încurcă exact ce ai de citit: un
   *  caroiaj de dale sub un traseu electric, lamelele unui parchet sub cote.
   *  Deci se stinge dintr-un buton, ca axele și etichetele, și camerele rămân
   *  hârtie albă. Nu e o editare: finisajele stau în document, neatinse. */
  araPardoseli?: boolean;
  /** The electrical layer's opacity: 1 draws it as always, 0 leaves it out,
   *  anything between fades it so walls and cotes read through. View state
   *  like `araPardoseli`, never in the document. A faded apparatus is not
   *  grabbable: what is dimmed is being looked past, not edited. */
  electricsOpacity?: number;
  /** Told the box chains as drawn, placed around the plan's text: a selected
   *  box's blue cotes must clear these, not an estimate of them. */
  laLanturi?: (l: readonly LantDoza[]) => void;
  /** Câți pixeli are un metru pe ecran — din ea se decide ce hașură se mai
   *  citește și ce etichetă mai încape. */
  pxPerM: number;
  interactiv?: boolean;
  /** Cursorul peste un zid. În editor e «move», fiindcă acolo chiar se poate
   *  trage de el; în vizualizator rămâne «pointer». */
  cursorPerete?: string;
  cuMobilier?: boolean;
}) {
  /** ÎNTINDEREA MĂȘTII CU CARE SE TAIE GOLURILE DIN ZIDURI — cadrul desenului
   *  LĂRGIT PESTE ZIDURI, cu un metru în jur.
   *
   *  Masca era cât `plan.cadru`, adică cât anvelopa, iar ce nu e acoperit de
   *  mască nu se desenează deloc. Deci orice zid de dincolo de amprenta
   *  modulului era șters de pe planșă: o cameră nouă trasă ALĂTURI de modul —
   *  cum se trage una ca să se vadă cum ar arăta — își primea cei patru pereți
   *  în document (34 -> 38 pe M2, numărați în panou) și rămânea pe desen un
   *  dreptunghi gol, fără niciun zid în jur.
   *
   *  `plan.cadru` NU poate crește ca să-i cuprindă: el e cadrul ANVELOPEI, iar
   *  pe el se scriu cotele de ansamblu ale conturului (v. `cote`). Masca însă
   *  nu măsoară nimic — e doar o foaie din care se decupează golurile —, deci
   *  se întinde peste tot ce maschează. */
  /** The mounting boxes' host thicknesses: the drawn depth cap (one wall pass
   *  per box, so memoised on the plan). */
  const grosimiDoze = useMemo(() => grosimileDozelor(plan), [plan]);

  const masca = useMemo(() => {
    // ȘI PIESELE INTRĂ ÎN EA, nu doar zidurile.
    //
    // Lecția de mai sus era învățată pe jumătate: masca fusese lărgită peste
    // ZIDURI, fiindcă un zid tras afară din amprentă se ștergea de pe planșă.
    // O piesă dusă afară pățea exact la fel, și tot nevăzută: ea rămâne în
    // document și în DOM — măsurat, 165 de piese în document și 165 desenate —
    // dar cade în afara măștii, deci nu se vede. «Când mut un obiect în afara
    // planului mi-l ascunde» (26 și 27.10.2026, a doua oară).
    //
    // `cadrulDesenului` e CHIAR reuniunea asta, scrisă o dată pentru
    // încadrare: anvelopa plus tot ce se desenează. Masca o ia de-acolo, ca
    // să nu existe două păreri despre „unde se întinde desenul".
    const c = cadrulDesenului(plan);
    return { x: c.x - 1, y: c.y - 1, w: c.w + 2, h: c.h + 2 };
  }, [plan]);

  /** Hașurile, calculate o dată pe geometrie: sunt sute de segmente per modul
   *  și se redesenează la fiecare mișcare de mouse altfel. */
  /** ID-UL MĂȘTII GOLURILOR, al acestui desen. Era `ob-goluri` pentru toate,
   *  iar pe «Toate corpurile» pânza ține câte un desen pe corp: browserul
   *  lua masca PRIMULUI, a unui vecin, care acoperă doar cutia vecinului — și
   *  pereții corpului de pe masă, căzuți în afara ei, nu se mai vedeau deloc
   *  (măsurat pe studio: trei măști cu același id, C3 la 88 m de a lui C2). */
  const idMasca = `ob-goluri-${useId().replace(/[^A-Za-z0-9_-]/g, "")}`;
  /** A DOUA MASCĂ, a CONTURURILOR de cameră.
   *
   *  Nu e o copie a celei de ziduri: taie ALTĂ suprafață, și de-aia există.
   *  Zidul se taie cât golul — atât lipsește din el. Conturul camerei trebuie
   *  tăiat cât PODEAUA din deschidere, care e mai lată (scobitura camerei) și
   *  mai adâncă (până la fața ei), plus o ieșire cât să prindă și grosimea
   *  liniei. Cu o singură mască, una din cele două iese greșit: cât golul,
   *  conturul rămâne ca o cutie peste pardoseală; cât podeaua, masca mănâncă
   *  din zid și rămân dungi albe la montanți.
   *
   *  Nu pot să se despartă: amândouă ies din aceeași funcție
   *  (`masculGolului`), cu și fără praguri. */
  const idMascaPodea = `ob-podea-${useId().replace(/[^A-Za-z0-9_-]/g, "")}`;
  const hasuri = useMemo(() => plan.pereti.map((w): HasuraPerete | null => {
    if (w.subTaietura) return null;      // nesecționat: nu poartă hașură
    // STICLA NU SE HAȘUREAZĂ, oricare ar fi hașura celorlalți. `parapetiiDeSticla`
    // o prinde din GEOMETRIE (parapet pe conturul unei camere), iar asta scapă
    // un parapet de sticlă declarat ca atare dar așezat altfel — până acum el
    // lua hașura implicită, subțire, și trecea neobservat; de când toți pereții
    // poartă aceeași hașură deasă, el ieșea NEGRU. Cerut să rămână cum era
    // (19.10.2026): sticla se vede prin ea, deci nu se umple.
    if (w.kind === "parapet_sticla" || w.material === "sticla") return null;
    // STRATURILE NU SE MAI DESENEAZĂ, DEOCAMDATĂ.
    //
    // Un zid care își declară alcătuirea avea câte o hașură PE STRAT, cu
    // benzile despărțite între ele (v. `design/straturi.ts`). Pe planșă asta
    // se citește ca niște LINII în grosimea zidului, iar arhitectul a cerut
    // de două ori contrariul: «The walls are still lined and not just black
    // hash (...) with a simple black hash not lines» (19.10.2026, a doua
    // oară, cu poză). Prima dată am schimbat numai hașura de ROL și am lăsat
    // ramura asta în picioare — adică exact zidurile cu straturi, care sunt
    // cele din modulele desenate de ea, au rămas dungate. De-aia s-a întors
    // cererea.
    //
    // CE SE PIERDE: desenul alcătuirii. Datele NU — `straturile(w)` rămâne
    // scris în document, panoul le arată și le editează mai departe, iar
    // metrajul și fațada le citesc de-acolo. Se întoarce ștergând blocul
    // ăsta; de-aia stă întreg, nu risipit.
    //   const benzi = benzileStraturilor(w);
    //   if (benzi.length) {
    //     return { benzi: benzi.map((b) => ({
    //       pas: hasuraStratului(b.strat.kind).pas, gros: b.strat.grosime,
    //       inel: b.inel, d: caleaStratului(b.strat.kind, b.inel, b.unghi) })) };
    //   }
    // O SINGURĂ HAȘURĂ, PENTRU ORICE PERETE (19.10.2026, cerut de arhitect).
    //
    // Rolul și materialul rămân în document și în model — pe ele stau FEM-ul și
    // armarea —, dar pe PLANȘĂ nu se mai citesc: toți pereții se desenează
    // arhitectural. Ca să se întoarcă la hașura pe rol, se pune la loc rândul
    // de dedesubt, care e lăsat anume.
    //   const fel = (w.material === "polistiren" ? HASURA_ROL.polistiren : null)
    //     ?? HASURA_ROL[w.kind] ?? HASURA_IMPLICITA;
    // (`HASURA_ROL` și `HASURA_IMPLICITA` stau mai departe în `plan/hasura`,
    //  nemișcate; se adaugă la loc în import.)
    const fel = HASURA_ARHITECTURALA;
    return { fel, d: caleHasura(fel.unghiuri.flatMap(
      (u) => liniiHasura(w.inel as number[][], fel.pas, u))) };
  }), [plan.pereti]);

  /** Grosimea peretelui-gazdă al unui gol.
   *
   *  ȘTERSĂTURA TREBUIE SĂ FIE CÂT ZIDUL PE CARE ÎL TAIE. Era un dreptunghi de
   *  0,68 m adâncime, alb, pus la fel pentru orice gol — iar zidurile din
   *  catalog au între 0,10 și 0,45, cele mai multe 0,15 sau 0,30. Pe o
   *  compartimentare de 15 cm, ștersătura intra cu 26,5 cm ÎN FIECARE DINTRE
   *  CAMERELE DE ALĂTURI și le ștergea de-acolo fondul, hașura și orice zid
   *  care trecea prin apropiere. Asta e „baza albă" din feedback — și nu se
   *  repară cu un PNG: un raster s-ar încețoșa între 2 și 90 px/m și ar veni
   *  tot cu un dreptunghi în jurul lui. Se repară măsurând zidul. */
  /** GROSIMEA PE CARE O TAIE GOLUL — a zidului lui ȘI a celor lipite de el.
   *
   *  Un gol își știe peretele-gazdă, deci se desena cât el. Dar pe fațadele
   *  dublate — un zid de structură cu unul de închidere lipit de fața lui —
   *  fereastra trece prin AMÂNDOUĂ, iar desenată cât unul singur ieșea o
   *  fereastră pe jumătate de perete. La fel zona de apucat, care se
   *  socotește din aceeași cifră: apăsai pe jumătatea din afară a ferestrei și
   *  nu se selecta nimic. Cerut: «on the walls that are doubled the window
   *  should be the width of both» și «when i select it doesn't select the
   *  whole window».
   *
   *  Se măsoară geometric, nu din document: se proiectează fețele zidurilor pe
   *  NORMALA golului și se crește intervalul cât timp zidurile se ating. Un
   *  zid care doar trece pe lângă — perpendicular, sau deplasat pe lungul
   *  golului — nu intră: trebuie să acopere golul pe toată lățimea lui. */
  const grosimeaGazdei = useMemo(
    () => (g: Gol) => grosimeaGazdeiPura(plan.pereti, g), [plan.pereti]);

  /** PARAPEȚII DE STICLĂ, pe indici — regula stă în `doc`, ca s-o poată
   *  citi și probele fără să randeze. */
  const parapetiiDeSticla = useMemo(
    () => parapetiiDeSticlaAi(plan.camere, plan.pereti),
    [plan.pereti, plan.camere]);

  /** PARDOSEALA INTRĂ ÎN UȘĂ PÂNĂ LA MIJLOCUL TOCULUI.
   *
   *  Inelul unei camere se oprește pe fața zidului, deci golul ușii rămânea
   *  fără pardoseală: o dungă albă între două camere, lată cât zidul. Regula
   *  de șantier e alta — «pardoseala se termină fix pe mijlocul tocului de
   *  ușă» —, și e și singurul loc în care se VEDE unde se schimbă finisajul
   *  dintre două camere.
   *
   *  GEOMETRIA NU SE MAI SOCOTEȘTE AICI: jumătățile sunt `plan.praguri`,
   *  derivate o dată (v. `doc::pragurileUsilor`), fiindcă din ele iese ȘI
   *  aria camerei. Socotite a doua oară în desen, cele două s-ar fi putut
   *  despărți fără ca nimic să se plângă — și atunci planșa ar fi arătat
   *  altă podea decât cea scrisă pe etichetă. Aici rămâne doar gruparea pe
   *  cameră, ca desenul să le picteze cu finisajul EI: altfel s-ar vedea un
   *  petic de altă culoare în prag.
   */
  const peticeleUsilor = useMemo(() => {
    const out = new Map<number, Pt[][]>();
    for (const p of plan.praguri ?? []) {
      const ale = out.get(p.camera) ?? [];
      ale.push(p.inel);
      out.set(p.camera, ale);
    }
    return out;
  }, [plan.praguri]);

  /** GLAFURILE FERESTRELOR, grupate la fel — și la fel folosite.
   *
   *  Aceeași dungă albă ca la ușă, lângă geam de data asta: inelul camerei se
   *  oprește pe fața zidului, iar în golul ferestrei nu rămânea nimic desenat.
   *
   *  INTRĂ ȘI ÎN APRINDERE, de la 01.10.2026. Nu intrau, fiindcă glaful nu se
   *  socotea în arie, iar regula de mai jos e «ce se aprinde e exact ce se
   *  socotește». Acum se socotește (v. `doc::pervazurileFerestrelor`), deci se
   *  și aprinde — regula a rămas, s-a mutat sub ea cealaltă hotărâre. */
  const peticeleFerestrelor = useMemo(() => {
    const out = new Map<number, Pt[][]>();
    for (const p of plan.pervazuri ?? []) {
      const ale = out.get(p.camera) ?? [];
      ale.push(p.inel);
      out.set(p.camera, ale);
    }
    return out;
  }, [plan.pervazuri]);

  /** PRAGURILE UȘILOR, indexate pe GOL — de ele are nevoie masca, ca să taie
   *  conturul camerelor pe toată deschiderea unei UȘI (v. `masculGolului`).
   *  Indicele e SCRIS în prag (`Prag.gol`), nu dedus din vârfuri.
   *
   *  NUMAI UȘILE, și asta e o alegere. Printr-o ușă camera CONTINUĂ — de-aia
   *  cele două își împart deschiderea la mijloc și de-aia conturul nu se
   *  desenează acolo: tras, ar fi chiar cutia din jurul golului care s-a cerut
   *  scoasă. La fereastră camera se OPREȘTE, iar muchia ei e tocmai ce trebuie
   *  văzut: «we should still have the dark blue edge where the light blue one
   *  ends» (01.10.2026). Deci golul ferestrei nu se maschează, iar conturul
   *  care trece pe-acolo e cel al `conturulCamerei` de mai jos — fața zidului,
   *  pe linie dreaptă. */
  const pragurileGolului = useMemo(() => {
    const out = new Map<number, Prag[]>();
    for (const p of plan.praguri ?? []) {
      out.set(p.gol, [...(out.get(p.gol) ?? []), p]);
    }
    return out;
  }, [plan.praguri]);

  /** CONTURUL UNEI CAMERE E MARGINEA A CE E PODIT, nu inelul brut.
   *
   *  Inelul scris se retrage în dreptul fiecărui gol — scobitura e amprenta
   *  tocului, vreo 8 cm —, iar peticele (pragul ușii, glaful ferestrei) o umplu
   *  la loc. Desenat pe inel, conturul cobora în cameră pe toată lățimea
   *  golului și lăsa o treaptă albastră lângă geam, deși podeaua de sub ea era
   *  a camerei. Desenat pe REUNIUNE, merge pe fața zidului, intră în deschidere
   *  până unde intră pardoseala și se întoarce — adică exact pe muchia
   *  aprinderii: «we should still have the dark blue edge where the light blue
   *  one ends».
   *
   *  Peste gol îl taie masca, dar numai la UȘI (v. mai sus).
   *
   *  Se socotește o dată pe document, nu la fiecare randare: e o reuniune de
   *  poligoane pe fiecare cameră. */
  const conturulCamerei = useMemo(() => {
    const petice = new Map<number, Pt[][]>();
    for (const q of [...(plan.praguri ?? []), ...(plan.pervazuri ?? [])]) {
      petice.set(q.camera, [...(petice.get(q.camera) ?? []), q.inel]);
    }
    return plan.camere.map((c, i) => conturulPodit(c.inel, petice.get(i) ?? []));
  }, [plan.camere, plan.praguri, plan.pervazuri]);

  /** Ce poate fi apucat în afară de camere. Sub pensulă, nimic. */
  const prindeObiecte = interactiv && !pensula;

  // Limba: numele camerelor vin prin `numeleCamerei`, care citește limba
  // curentă; hook-ul e chemat ca desenul să se refacă atunci când ea se
  // schimbă, nu la următoarea atingere a documentului. Tot el scrie și numele
  // bucăților de nucleu din tooltip.
  const t = useT();

  const asezat = useAsezat(pxPerM, plan);
  const pxAdnotare = asezat.px;
  const planAdnotare = asezat.plan;

  /** ETICHETELE CAMERELOR — v. `etichetelePlanului`. */
  const etichete = useMemo(() => etichetelePlanului(planAdnotare, pxAdnotare, mod),
                           [planAdnotare, pxAdnotare, mod]);

  /** COTELE PLANȘEI, așezate după etichete — regulile stau în `cote.ts`. Numai
   *  pe planșa tehnică: în design desenul poartă deja mobilier și instalație,
   *  iar cotele sunt ale pereților, nu ale amenajării. */
  /** Cheia cotelor stinse, ca text — v. nota din lista memo-ului de mai jos. */
  const cheiaStinselor = (plan.coteStinse ?? []).join("\u0001");
  const prinseAparate = useMemo(
    () => (prindeObiecte && electricsOpacity >= 1
      ? plan.electrice.flatMap((a) => prinsaAparatului(a, pxPerM) ?? []) : []),
    [prindeObiecte, electricsOpacity, plan.electrice, pxPerM]);

  const cote = useMemo(() => {
    if (mod === "design" || !araCote) return [];
    const ocupate: Cutie[] = araEtichete
      ? etichete.flatMap((e) => (e ? [e.cutie] : [])) : [];
    // ZIDUL ALES intră în socoteala cotelor: una care dă în el îl va MUTA PE
    // EL când i se scrie altă cifră (v. `cote::cuZidulEi`).
    // COTELE STINSE SUNT ÎN AFARA RAGAZULUI, ca `ales` (v. nota lui
    // `useAsezat`): stinsul unei cote e un FAPT SINGUR, nu un șuvoi ca
    // tragerea. Luat din planul întârziat, el ajungea pe desen abia la
    // următorul eveniment — apăsai «×», nu se întâmpla nimic, și cota pierea
    // de-abia când mișcai mouse-ul. Măsurat: după «×» și 900 ms, cota tot
    // acolo; o mișcare de mouse care nu schimbă nimic o făcea să dispară.
    // «Încă nu pot să șterg cote cu x» (27.10.2026).
    //
    // AȘEZAREA RĂMÂNE A PLANULUI ÎNTÂRZIAT: ea e cea scumpă, și tocmai ea
    // trebuie să stea pe loc cât ține o tragere.
    const asezatCuStinse = { ...planAdnotare, coteStinse: plan.coteStinse };
    return cotelePlanului(asezatCuStinse, pxAdnotare, ocupate,
                          ales?.fel === "perete" ? ales.i : null);
    // `ales` E ÎN LISTĂ, altfel cotele nu s-ar reface la schimbarea selecției
    // și zidul ales n-ar prelua niciodată cota — adică tocmai ce s-a cerut.
    // `pxAdnotare`, NU `pxPerM`: aceeași mărire cu a etichetelor, altfel cotele
    // s-ar feri de niște cutii de etichetă socotite la altă mărire decât a lor.
    // CHEIA LISTEI, NU LISTA: `deriva` face un ARRAY NOU la fiecare derivare,
    // deci pus ca atare în listă el ar fi recalculat adnotarea la fiecare pas
    // de tragere — chiar lucrul pe care răgazul îl păzește (v.
    // `adnotareAsezata.test`). Scrisă ca text, cheia se schimbă doar când
    // omul chiar stinge sau reaprinde o cotă.
  }, [planAdnotare, pxAdnotare, mod, araCote, araEtichete, etichete, ales,
      cheiaStinselor]);

  /** What the box annotations must keep off: the room names and the plan's
   *  own cotes, figures and lines. They give way, never the other way round: the plan's cotes
   *  fall silent when they cannot be placed, and a socket must not cost a
   *  room its dimensions. */
  const ocupateDoze = useMemo<Cutie[]>(() => [
    ...(araEtichete ? etichete.flatMap((e) => (e ? [e.cutie] : [])) : []),
    ...cote.flatMap((c) => [cutiaCifrei(c, pxPerM), cutiaLiniei(c, pxPerM)]),
  ], [etichete, araEtichete, cote, pxPerM]);
  /** The boxes' dimension chains — permanent, like the plan's own cotes, and
   *  part of the electrics block so the layer toggle carries them too. */
  const lanturiDoze = useMemo(() => coteleDozelor(plan, pxPerM, ocupateDoze),
                              [plan, pxPerM, ocupateDoze]);
  useEffect(() => { laLanturi?.(lanturiDoze); }, [laLanturi, lanturiDoze]);
  /** Glyphs whose «H=» would fall under a door tag or a room name. */
  const hDreaptaDoze = useMemo(
    () => dozeleCuEtichetaLaDreapta(plan, pxPerM, ocupateDoze),
    [plan, pxPerM, ocupateDoze]);

  /** CÂT E UȘA ALEASĂ, în culoarea selecției.
   *
   *  Măsura ei e deja pe planșă, ca a oricărei uși (v. `cote::cotelePlanului`,
   *  regula 10) — aici se scoate din listă și se scrie încă o dată, albastră:
   *  neagră peste albastră ar fi două rânduri de cifre unul peste altul.
   *
   *  ȘI SE SCRIE CHIAR CÂND PLANȘA A TĂCUT-O: la mărire mică numărul ușii nu
   *  încape, dar cine a ales o ușă tocmai despre ea întreabă. Tot de-aia nu
   *  atârnă de `mod` sau de `araCote`: alea sting cotele PLANȘEI, nu răspunsul
   *  la o selecție. */
  /** COTA ALEASĂ de pe planșă — recunoscută după CHEIE, nu după indice: ea se
   *  naște la fiecare desenare, iar locul ei în listă se schimbă cu mărirea
   *  (v. `Ales` și `cote::cheiaCotei`). */
  const cotaAleasa = useMemo(() => (
    ales?.fel === "cota"
      ? cote.find((c) => cheiaCotei(c) === ales.cheie) ?? null : null
  ), [cote, ales]);

  /** COTELE CARE AȘAZĂ GOLUL ALES între pereții de lângă el — v.
   *  `cote::coteleAsezariiGolului`. Se scriu numai pe selecție: pe planșă ar
   *  fi două cifre în plus la fiecare ușă. */
  const coteGolAles = useMemo(
    () => (ales?.fel === "gol"
      ? coteleAsezariiGolului(plan, ales, COTA_PX.gol / pxPerM) : []),
    [plan, ales, pxPerM]);

  const cotaUsaAleasa = useMemo(() => {
    if (ales?.fel !== "gol") return null;
    // ȘI FEREASTRA ALEASĂ ÎȘI SCRIE MĂSURA. Planșa cotează numai ușile
    // (regula 10), fiindcă o fereastră își are cifrele în panou — dar cât o
    // TRAGI de capăt, panoul e departe de mână: «atunci când vreau să măresc
    // sau să micșorez o fereastră mi-ar plăcea să văd și dimensiunea ei în
    // timp real» (21.10.2026). Documentul se schimbă la fiecare pas al
    // gestului, deci cifra scrisă aici se rescrie odată cu el, fără nimic
    // anume pentru tragere.
    //
    // `usa` NUMAI PE UȘI: indicele e al listei lui, iar o fereastră cu același
    // număr ar fi cules cota unei uși care nu e a ei.
    const gata = ales.fel2 === "doors"
      ? cote.find((c) => c.usa === ales.i) : undefined;
    return gata ?? cotaGolului(plan, ales, COTA_PX.gol / pxPerM);
  }, [cote, plan, ales, pxPerM]);

  /** Apăsarea care ALEGE ceva. Numai butonul stâng, și numai când nu se
   *  panoramează: mijlocul, dreapta și spațiul sunt ale pânzei de dedesubt, iar
   *  oprite aici n-ar mai ajunge niciodată la ea. */
  const apuca = (a: Ales) => (e: React.PointerEvent) => {
    if (!interactiv || !laAles || panorameaza || e.button !== 0) return;
    e.stopPropagation();
    laAles(a, e);
  };

  return (
    <>
      {/* ANVELOPA NU SE MAI DESENEAZĂ. Era dreptunghiul alb de sub tronson —
          spațiul construibil, ce nu e nici cameră nici perete. Pe un modul care
          nu-și umple conturul arăta ca o cameră pe care uitase cineva s-o
          deseneze, nu ca o măsură. Scos la cerere: «delete the rectangle for
          the buildable space, we should not have it anymore».

          `plan.anvelopa` RĂMÂNE în plan: din el ies conturul și încadrarea
          (v. `Panza2D::useIncadrare`). Pleacă doar tușul lui. */}

      {/* Rosturile finisajelor, o singură dată — le folosesc amândouă vederile. */}
      <defs><FinisajeDefs /></defs>

      {/* camerele */}
      {plan.camere.map((c, i) => {
        const sel = (ales?.fel === "camera" && ales.i === i)
          || !!alese?.includes(i);
        // ÎN DESIGN, culoarea camerei e pardoseala ei. PE PLANȘĂ nu există
        // culoare (v. nota de la `ROST_PE_PLANSA`): hârtie albă și rostul
        // pardoselii în gri.
        const fin = finisajul(c.pardoseala);
        const plansa = mod !== "design";
        // O ZONĂ e desenată PESTE camera ei, deci conturul ei e singurul lucru
        // care o arată — v. `ZONA_CONTUR`.
        const eZona = !!conturZonei(c.kind);
        return (
          <g key={`c${i}`}>
            {/* HÂRTIA ȘI APUCAREA, pe inelul brut: ce se poate apăsa e corpul
                camerei, iar peticele din goluri se desenează oricum deasupra.
                Conturul s-a mutat pe propriul lui `path`, mai jos. */}
            <path d={cale(c.inel)}
                  fill={HARTIE}
                  stroke="none"
                  /* «pointer», nu «move»: corpul camerei se alege, nu se trage.
                     O cameră se redimensionează doar de margini (v. nota din
                     `editor/Panza`), iar un cursor de mutare ar promite un gest
                     care nu mai există. */
                  style={{ cursor: interactiv ? "pointer" : "inherit" }}
                  onPointerDown={apuca({ fel: "camera", i })} />
            {/* CONTURUL, PE MARGINEA A CE E PODIT (v. `conturulCamerei`), și
                tăiat în dreptul UȘILOR. Inelul camerei trecea drept prin
                fiecare deschidere: acolo se vedeau trei linii — față, ax, față
                —, iar cea de la mijloc, singura care spune unde se schimbă
                pardoseala, se pierdea între ele. Cerut scos (28.09.2026).

                La FERESTRE nu se mai taie: acolo camera chiar se termină, iar
                muchia ei e ce trebuie văzut (01.10.2026).

                ACEEAȘI MASCĂ CA A ZIDURILOR, nu una nouă: golurile sunt deja
                decupate acolo, iar două măști ar fi trebuit ținute la fel la
                fiecare schimbare de grosime. */}
            {conturulCamerei[i].map((inel, j) => (
              <path key={`ct${j}`} d={cale(inel)}
                    fill="none"
                    stroke={sel ? SEL
                            : conturZonei(c.kind)?.stroke ?? TUS_CULOARE}
                    strokeWidth={sel ? 3
                                 : conturZonei(c.kind)?.strokeWidth
                                   ?? TUS.subtire}
                    /* MASCA GOLURILOR NU E PENTRU ZONE. Ea taie conturul unei
                       camere în dreptul ușilor, acolo unde pardoseala curge
                       mai departe. O zonă n-are uși pe laturile ei, dar dacă o
                       latură trece întâmplător prin dreptul uneia, bucata aia
                       de contur dispărea — iar un triunghi cu o latură ruptă
                       nu mai arată a zonă. */
                    mask={eZona ? undefined : `url(#${idMascaPodea})`}
                    vectorEffect="non-scaling-stroke"
                    pointerEvents="none" />
            ))}
            {/* SELECȚIA SE VEDE ȘI CA FOND, nu doar ca un contur mai gros.
                Conturul singur ajungea cât timp se alegea o cameră; cu mai
                multe alese deodată, două chenare albastre printre nouă camere
                se pierd — iar întrebarea «pe care le unesc?» se pune tocmai
                atunci. Foarte stins, ca să rămână planșă. */}
            {c.flat != null && culoriApartamente?.[c.flat] && (
              <path d={cale(c.inel)} fill={culoriApartamente[c.flat]}
                    pointerEvents="none" />
            )}
            {/* ALEASĂ, CAMERA SE APRINDE CU PRAGURILE EI CU TOT.
                Aria scrisă pe etichetă cuprinde jumătatea ei din fiecare
                deschidere (v. `doc::pragurileUsilor`), iar aprinsă numai pe
                inel, camera arăta mai mică decât cifra ei: în fiecare ușă
                rămânea o pată stinsă. Cerut de două ori — «when we select a
                room it includes the half of the doorway on its side … have it
                shown like highlighted». Aceleași petice ca ale pardoselii, deci
                ce se aprinde e exact ce se socotește.

                ȘI GLAFURILE FERESTRELOR, de la 01.10.2026: «the room area and
                highlight should include that white space next to the window».
                Intră acum și în arie, deci regula de deasupra ține mai departe
                — v. `doc::pervazurileFerestrelor`. */}
            {sel && (
              <>
                <path d={cale(c.inel)} fill={SEL} fillOpacity={0.09}
                      pointerEvents="none" />
                {[...(peticeleUsilor.get(i) ?? []),
                  ...(peticeleFerestrelor.get(i) ?? [])].map((inel, j) => (
                  <path key={`sp${j}`} d={cale(inel)} fill={SEL}
                        fillOpacity={0.09} pointerEvents="none" />
                ))}
              </>
            )}
            {/* Fondul și rostul, peste hârtie și fără să prindă clickuri: un
                al doilea strat care ar prinde ar fi furat apucarea camerei de
                dedesubt. */}
            <g opacity={plansa ? 1 : PODEA_IN_DESIGN}>
              {fin && !plansa && araPardoseli && (
                <path d={cale(c.inel)} fill={fin.fond} pointerEvents="none" />
              )}
              {fin && araPardoseli && rostVizibil(fin, pxPerM) && (
                <path d={cale(c.inel)} fill={`url(#${patternul(fin.cheie, plansa)})`}
                      fillOpacity={plansa ? ROST_PE_PLANSA : 1}
                      pointerEvents="none" />
              )}
              {/* …ȘI PRAGURILE ei: aceeași pardoseală, dusă până la mijlocul
                  tocului fiecărei uși. Desenate la fel, ca să nu se vadă
                  unde se termină camera și începe pragul. */}
              {/* PARDOSEALA INTRĂ ÎN GOL ȘI PE PLANȘĂ — cerut explicit
                  (28.09.2026): «I want the two different flooring in the
                  doorway in Plan … no empty space ever there».

                  ASTA SCHIMBĂ DOUĂ REGULI VECHI, și de-aia scrie aici care
                  anume. Planșa era ALB-NEGRU (v. `doc.test`), iar în golul
                  ușii nu se picta nimic, fiindcă golul e o gaură, nu hârtie
                  peste zid (v. `gol.test`, `feedback.test` — regula venită
                  din feedback pe baza albă a iconului de ușă).

                  CE RĂMÂNE DIN ELE: peticul nu calcă zidul. Stă numai în
                  deschidere, de la fața camerei până la axul golului, deci
                  nu acoperă nici hașura, nici tocurile, nici foaia ușii.
                  Ce se pierde e strict monocromia planșei, în două
                  pătrățele cât un toc — și exact acolo se vede lucrul
                  cerut: unde se schimbă pardoseala dintre două camere. */}
              {fin && araPardoseli
                && [...(peticeleUsilor.get(i) ?? []),
                    ...(peticeleFerestrelor.get(i) ?? [])].map((q, n) => (
                <g key={`p${n}`}>
                  {/* FONDUL INTRĂ ȘI EL ÎN GOL, ÎN DESIGN — aceeași condiție
                      ca a camerei de deasupra (`fin && !plansa`), fiindcă e
                      aceeași pardoseală: jumătatea de ușă e a camerei, deci
                      se vede ca ea. Cerut pe 28.09.2026, arătând două
                      randări: «yes the second option».

                      PE PLANȘĂ NU. Acolo culoarea plină peste deschidere se
                      citea ca o pată — scoasă în aceeași zi: «just take out
                      these beige color on top, JUST THE BEIGE NOT THE PATTERN
                      UNDERNEATH». Cele două cereri nu se bat: planșa rămâne
                      alb-negru și arată despărțirea cu linia de pe ax, iar
                      designul arată pardoseala fiecărei camere. */}
                  {fin && !plansa && (
                    <path d={cale(q)} fill={fin.fond} pointerEvents="none" />
                  )}
                  {fin && rostVizibil(fin, pxPerM) && (
                    <path d={cale(q)}
                          fill={`url(#${patternul(fin.cheie, plansa)})`}
                          fillOpacity={plansa ? ROST_PE_PLANSA : 1}
                          pointerEvents="none" />
                  )}
                </g>
              ))}
            </g>
          </g>
        );
      })}

      {/* nucleul */}
      {/* NUCLEUL — și de ce se poate acum apăsa pe el.
          Era desenat într-un strat surd (`pointerEvents="none"`): scara și
          lifturile unui modul din catalog se vedeau, dar nu existau pentru
          mână. «Pare ceva desenat și nu se poate modifica» — chiar așa era.
          Măsurile lui stau de mult în `doc.core` (adâncimea scării, a puțului,
          câte lifturi, grosimea pereților lui); nimic nu le arăta. */}
      {plan.nucleu && (() => {
        const sel = ales?.fel === "nucleu";
        return (
          <g>
            <rect x={plan.nucleu.rect[0]} y={plan.nucleu.rect[1]}
                  width={plan.nucleu.rect[2] - plan.nucleu.rect[0]}
                  height={plan.nucleu.rect[3] - plan.nucleu.rect[1]}
                  fill={HARTIE} stroke={sel ? SEL : TUS_CULOARE}
                  strokeWidth={sel ? 3 : TUS.vazut}
                  vectorEffect="non-scaling-stroke"
                  style={{ cursor: prindeObiecte ? "pointer" : "inherit" }}
                  pointerEvents={prindeObiecte ? "all" : "none"}
                  onPointerDown={apuca({ fel: "nucleu" })} />
            {/* PĂRȚILE — scara, puțurile — sunt obiecte, nu desen: stau în
                `plan.core_parts`, de unde le citesc și motoarele când pun scara
                adevărată în clădire. Deci se aleg, se trag, se redimensionează
                și se șterg, ca orice altceva de pe planșă. */}
            {plan.nucleu.parti.map((p, i) => {
              const selP = ales?.fel === "nucleuParte" && ales.i === i;
              return (
                <path key={i} d={cale(p.inel)} fill={HARTIE}
                      stroke={selP ? SEL : TUS_CULOARE}
                      strokeWidth={selP ? 3 : TUS.subtire}
                      vectorEffect="non-scaling-stroke"
                      style={{ cursor: prindeObiecte ? "move" : "inherit" }}
                      pointerEvents={prindeObiecte ? "all" : "none"}
                      onPointerDown={apuca({ fel: "nucleuParte", i })}>
                  <title>{p.kind === "lift" ? t("prop.parte.lift")
                    : p.kind === "scara" ? t("prop.parte.scara") : p.kind}</title>
                </path>
              );
            })}
          </g>
        );
      })()}

      {/* GOLURILE SE TAIE DIN ZID, NU SE ACOPERĂ CU ALB.
          Ștersătura era un dreptunghi alb pus PESTE tot ce se desenase: peste
          zid, peste hașură, peste fondul camerelor de alături. Chiar potrivită
          pe grosimea zidului, rămânea o pată opacă — «are bază albă și ia din
          celelalte elemente, aș face ceva cu transparență».
          Ca mască, zidul pur și simplu nu se desenează acolo: prin gol se vede
          ce e dedesubt — pardoseala camerei, finisajul, orice — fiindcă nu s-a
          pus nimic deasupra.

          Se întinde peste TOT ce maschează, v. `masca`. */}
      <defs>
        <mask id={idMasca} maskUnits="userSpaceOnUse"
              x={masca.x} y={masca.y} width={masca.w} height={masca.h}>
          <rect x={masca.x} y={masca.y} width={masca.w} height={masca.h}
                fill="#fff" />
          {plan.goluri.map((g, i) => (
            <path key={i} d={cale(stersaturaGolului(g, grosimeaGazdei(g)))}
                  fill="#000" />
          ))}
        </mask>
        <mask id={idMascaPodea} maskUnits="userSpaceOnUse"
              x={masca.x} y={masca.y} width={masca.w} height={masca.h}>
          <rect x={masca.x} y={masca.y} width={masca.w} height={masca.h}
                fill="#fff" />
          {/* NUMAI UȘILE. Printr-o ușă camera continuă, iar conturul tras prin
              deschidere e chiar cutia care s-a cerut scoasă (28.09.2026). La
              FEREASTRĂ camera se termină: acolo conturul e muchia ei, și
              trebuie să se vadă — «we should still have the dark blue edge
              where the light blue one ends» (01.10.2026). */}
          {plan.goluri.map((g, i) => (g.fel === "usa" ? (
            <path key={i}
                  d={cale(masculGolului(g, grosimeaGazdei(g),
                                        pragurileGolului.get(i) ?? []))}
                  fill="#000" />
          ) : null))}
        </mask>
      </defs>

      {/* ROSTUL PARDOSELII, în mijlocul fiecărei uși.
          «Pardoseala se termină fix pe mijlocul tocului de ușă» (tester,
          28.09.2026) — iar pe planșă asta se ARATĂ printr-o linie, nu prin
          două culori: în convenția tehnică golul e alb de amândouă părțile,
          deci fără linie nu se vede nicăieri unde se schimbă finisajul.
          `g.a`—`g.b` E CHIAR AXUL GOLULUI: ștersătura se face din el, ±
          jumătate de grosime (v. `stersaturaGolului`), deci linia cade exact
          la mijlocul tocului. Se desenează înaintea pereților și nu se pierde
          sub ei: golul e decupat din zid cu masca, deci acolo peretele nu
          pictează nimic. */}
      {araPardoseli && plan.goluri.filter((g) => g.fel === "usa").map((g, i) => (
        <line key={`rost${i}`}
              x1={g.a[0]} y1={g.a[1]} x2={g.b[0]} y2={g.b[1]}
              stroke={TUS_CULOARE} strokeWidth={TUS.subtire}
              vectorEffect="non-scaling-stroke" pointerEvents="none" />
      ))}

      {/* PEREȚII, în convenția planșei tehnice */}
      <g mask={`url(#${idMasca})`}>
      {/* MOBILIERUL, SUB ZIDURI. Cerut de arhitect (26.10.2026): «pereții ar
          trebui să stea deasupra sau să pot să-i aduc și pe ei în față». Pe o
          planșă zidul e ce taie planul de secțiune, deci el e cel care
          acoperă: un dulap împins peste un perete îl ștergea de pe desen, iar
          hașura zidăriei se oprea sub piesă ca și cum acolo n-ar fi zid.
          Rămâne PESTE camere (pardoseala e sub tot) și SUB etichete, instalație
          și cote — numele camerei se citește peste el ca înainte. */}
      {cuMobilier && <Mobila plan={plan} pxPerM={pxPerM} ales={ales}
                             mobilaAlese={mobilaAlese}
                             apuca={apuca} interactiv={prindeObiecte} />}

      {/* RANGUL SCRIS BATE ORDINEA DIN DOCUMENT, ca la mobilier: două ziduri
          suprapuse se acopereau altfel în funcție de cine fusese scris întâi,
          iar «Adu în față» e singurul fel de a alege. `sort` e stabil, deci
          zidurile de același rang rămân în ordinea lor. Indicele ADEVĂRAT se
          duce cu fiecare, fiindcă selecția și apucarea sunt pe el. */}
      {plan.pereti.map((w, i) => [w, i] as const)
        .sort((a, b) => (a[0].rang ?? 0) - (b[0].rang ?? 0))
        .map(([w, i]) => {
        const sel = (ales?.fel === "perete" && ales.i === i)
          || !!pereteleAlese?.includes(i);
        const h = hasuri[i];
        // STICLA NU SE HAȘUREAZĂ și nu se umple: v. `parapetiiDeSticla`.
        const sticla = parapetiiDeSticla.has(i);
        // PERETELE-CORTINĂ SE DESENEAZĂ CA O TÂMPLĂRIE: sticla două linii
        // subțiri, iar panourile despărțite de montanți negri de-a latul
        // zidului — «it looks like window sections separated by black lines»
        // (20.10.2026). Nici poché (s-ar citi ca zidărie portantă acolo unde
        // nu e niciun zid), nici hașură (aia e tăietură prin zidărie).
        // V. `montantiiCortinei`.
        const cortina = w.material === "perete_cortina";
        // HAȘURA NU SE MAI DESENEAZĂ PE CE E UMPLUT PLIN: pe `ZID_POCHE` ar fi
        // linii negre pe negru, adică mii de noduri SVG care nu se văd. Rămâne
        // pentru ce NU e pochetat — azi nimic, mâine orice fel scos din poché.
        const vizibila = !sticla && !cortina && !!w.subTaietura && h?.fel
          && h.fel.pas * pxPerM >= MIN_PX_INTRE_LINII;
        // Montanții, cât se văd: sub un pixel și jumătate bara nu mai e o bară.
        const montanti = cortina && CORTINA_MONTANT * pxPerM >= 1.5
          ? montantiiCortinei(w.inel) : null;
        return (
          <g key={w.id}>
            {/* POCHÉ DOAR PE CE E TĂIAT, și plin (v. `ZID_POCHE`). Un parapet
                nu e tăiat de secțiune (se privește de sus, v. `subTaietura`),
                deci nu se umple: ar arăta ca un zid întreg pe un balcon
                deschis — și tocmai asta a cerut arhitectul să rămână afară,
                «mai puțin la balcon/terasă». Sticla la fel. */}
            <path d={cale(w.inel)}
                  /* STICLA ACOPERĂ CE E SUB EA, deși nu se hașurează. Era
                     `none` — adică transparentă —, iar pe un balcon cu
                     pardoseala desenată hașura trecea prin parapet, de parcă
                     n-ar fi fost nimic acolo. Cerut de arhitect (26.10.2026):
                     «parapetul de sticlă ar trebui să ascundă hașura de sub
                     el». Hârtie, nu poché: un parapet nu e tăiat de secțiune,
                     deci nu se umple plin — v. nota de mai sus. */
                  fill={sticla || w.subTaietura || cortina ? HARTIE : ZID_POCHE}
                  stroke={sel ? SEL : TUS_CULOARE}
                  strokeWidth={sel ? 3 : sticla || cortina ? TUS.subtire
                    : w.subTaietura ? TUS.vazut : TUS.taiat}
                  vectorEffect="non-scaling-stroke"
                  style={{ cursor: prindeObiecte ? cursorPerete : "inherit" }}
                  pointerEvents={prindeObiecte ? "all" : "none"}
                  onPointerDown={apuca({ fel: "perete", i })} />
            {vizibila && (
              <path d={h!.d} stroke={sel ? SEL : TUS_CULOARE}
                    strokeWidth={TUS.subtire} fill="none"
                    vectorEffect="non-scaling-stroke" pointerEvents="none" />
            )}
            {montanti && (
              <path d={montanti} fill={sel ? SEL : TUS_CULOARE}
                    stroke="none" pointerEvents="none" />
            )}
            {/* STRATURILE, fiecare la mărimea lui — aceeași regulă ca a
                hașurilor, și din același motiv.

                CONTURUL BENZII e ce desenează liniile de despărțire dintre
                straturi: nu le trasează nimeni separat, ele sunt chiar
                marginile comune a două benzi. Sub un pixel și jumătate de
                grosime însă, banda nu mai e o bandă, e o a doua linie lipită de
                conturul zidului — iar un perete cu patru straturi subțiri
                ajunge, la scara modulului, o mâzgăleală în loc de un zid. Deci
                dispare, cum dispare și hașura ei sub `MIN_PX_INTRE_LINII`. */}
            {!sticla && h?.benzi?.map((b, k) => (b.gros * pxPerM < MIN_PX_BANDA ? null : (
              <g key={`s${k}`} pointerEvents="none">
                <path d={cale(b.inel)} fill="none"
                      stroke={sel ? SEL : TUS_CULOARE} strokeWidth={TUS.subtire}
                      vectorEffect="non-scaling-stroke" />
                {b.pas * pxPerM >= MIN_PX_INTRE_LINII && (
                  <path d={b.d} stroke={sel ? SEL : TUS_CULOARE}
                        strokeWidth={TUS.subtire} fill="none"
                        vectorEffect="non-scaling-stroke" />
                )}
              </g>
            )))}
          </g>
        );
      })}

      </g>

      {/* GOLURILE, peste pereți: semnul de ușă sau de fereastră */}
      {plan.goluri.map((g, i) => {
        const usa = g.fel === "usa";
        const lista = usa ? ("doors" as const) : ("windows" as const);
        const nr = plan.goluri.slice(0, i).filter((x) => x.fel === g.fel).length;
        const sel = (ales?.fel === "gol" && ales.fel2 === lista && ales.i === nr)
          || !!golurileAlese?.some((x) => x.fel2 === lista && x.i === nr);
        const grosime = grosimeaGazdei(g);
        const sterge = stersaturaGolului(g, grosime);
        return (
          <g key={`g${i}`}>
            {usa ? <Usa g={g} grosime={grosime} gros={factorTus(pxPerM)} />
                 : <Fereastra g={g} grosime={grosime} />}
            {/* zona de apucat, peste tot ce s-a desenat — v. `zonaGolului` */}
            {/* GROSIMEA DE LINIE ZERO, ALTFEL ZONA ARE O MARGINE DE UN METRU.
                `pointerEvents="all"` face să răspundă și conturul, nu doar
                interiorul, ORICÂT de nevăzut ar fi el — iar linia nedeclarată
                are grosimea implicită de 1 UNITATE DE DESEN, adică 1 m aici.
                Deci în jurul fiecărei uși stătea o bandă invizibilă de o
                jumătate de metru: la 37 px/m, 18 px de fiecare parte; la
                mărirea la care se lucrează, ~50. Apăsai pe zidul de lângă ușă
                și se alegea ușa. Măsurat în editor pe M8: din punctele
                încercate pe corpul zidurilor, 996 nimereau un gol. */}
            <path d={cale(zonaGolului(g, grosime, sel))}
                  /* CINE E, pentru probe: zona de apucare e un `path` fără
                     nimic de recunoscut, iar gesturile pe goluri se probează
                     pe ea — altfel proba apasă pe primul dreptunghi găsit și
                     trece oricum, v. `fereastraGest`. */
                  data-gol={`${lista}-${nr}`}
                  fill="transparent" strokeWidth={0}
                  style={{ cursor: prindeObiecte ? "pointer" : "inherit" }}
                  pointerEvents={prindeObiecte ? "all" : "none"}
                  onPointerDown={(e) => {
                    // Aceeași regulă ca în `apuca`: golul nu apucă butonul care
                    // panoramează, altfel pânza nu se mai poate muta de pe el.
                    if (!prindeObiecte || panorameaza || e.button !== 0) return;
                    e.stopPropagation();
                    laAles?.({ fel: "gol", fel2: lista, i: nr }, e);
                  }} />
            {sel && (
              <path d={cale(sterge)} fill="none" stroke={SEL} strokeWidth={3}
                    vectorEffect="non-scaling-stroke" pointerEvents="none" />
            )}
          </g>
        );
      })}

      {/* INSTALAȚIA ELECTRICĂ. Traseele întâi, aparatele peste ele — un fir
          desenat peste o priză se citește ca și cum ar intra în ea.

          APARATELE SE VĂD ȘI PE PLANȘĂ, nu doar în Design: paleta din stânga
          are grupa «Instalații electrice», iar o unealtă care pune ceva ce
          vederea nu arată e o unealtă care pare stricată. FIRELE rămân ale
          Designului — ele sunt o consecință desenată (un Dijkstra pe zidurile
          modulului), iar pe planșa de trasat ar acoperi tocmai cotele. */}
      {(() => {
        const electrics = (
          <>
            {trasee?.length ? (
              <Trasee trasee={trasee} aparate={plan.electrice}
                      alesIdx={ales?.fel === "aparat" ? ales.i : null} />
            ) : null}
            <Aparate aparate={plan.electrice} pxPerM={pxPerM}
                     alesIdx={ales?.fel === "aparat" ? ales.i : null}
                     interactiv={prindeObiecte && electricsOpacity >= 1}
                     apuca={(i) => apuca({ fel: "aparat", i })}
                     grosimi={grosimiDoze} hDreapta={hDreaptaDoze} />
            {lanturiDoze.map((l, i) => (
              <Cote key={i} cote={l.cote} k={pxPerM} culoare={INK_FAMILIE[l.fam]} />
            ))}
          </>
        );
        // The group exists only below 1: at 1 the markup must stay today's,
        // so the sheet and every existing test see no new element.
        return electricsOpacity >= 1 ? electrics
          : electricsOpacity > 0 ? <g opacity={electricsOpacity}>{electrics}</g>
          : null;
      })()}

      {/* NUMELE ȘI ARIA CAMEREI, apoi COTELE — v. `etichete` și `cote` de sus.
          Numele cu majuscule, aria dedesubt mai mică, amândouă cu tușul
          planșei; se așază pe cadrul DINĂUNTRU al camerei, ca pe un hol în L
          scrisul să nu cadă în scobitură. Cotele vin după ele și le ocolesc. */}
      <g pointerEvents="none">
        {araEtichete && <NumeleCamerelor etichete={etichete} />}
        {cote.length > 0 && (
          <Cote cote={cote.filter((c) => c !== cotaUsaAleasa && c !== cotaAleasa)}
                k={pxPerM} />
        )}
        {cotaUsaAleasa && (
          <Cote cote={[cotaUsaAleasa]} k={pxPerM} culoare={SEL} />
        )}
        {cotaAleasa && cotaAleasa !== cotaUsaAleasa && (
          <Cote cote={[cotaAleasa]} k={pxPerM} culoare={SEL} />
        )}
        {coteGolAles.length > 0 && (
          <Cote cote={coteGolAles} k={pxPerM} culoare={SEL} />
        )}
      </g>
      {/* ȚINTELE CIFRELOR, numai în editor (`laCota`). Cotele planșei se scriu
          ca oricare alta — cifra e locul firesc în care se cere o măsură —,
          dar desenul e și al vizualizatorului, care n-are ce face cu un clic.
          Pastila e a CIFREI, nu a liniei: pe o cotă lungă linia se nimerește
          greu, iar ochiul apasă oricum pe număr. */}
      {/* PE ORICE COTĂ, nu doar pe cele care mută un zid. Ținta era pusă numai
          pe alea (`muta && peZid`), deci cotele venite din desen nu se puteau
          nici apăsa — iar caseta de ștergere n-avea cum să se deschidă pe ele.
          Acum oricare se apasă: unele se și scriu, toate se sting. */}
      {/* ȘI COTELE GOLULUI ALES SE APASĂ — altfel s-ar vedea și nu s-ar putea
          scrie. Ele cer `pastreazaSelectia`: scrierea lor MUTĂ GOLUL, iar
          pentru asta golul trebuie să rămână cel ales (v. `Panza::scrieCota`,
          care mută `ales`). Fără semnul ăsta, apăsarea ar fi ales cota, iar
          cifra n-ar mai fi avut ce mișca. */}
      {laCota && coteGolAles.map((c, i) => (
        <circle key={`tg${i}`}
                cx={c.a[0] + (c.b[0] - c.a[0]) * (c.la ?? 0.5)}
                cy={c.a[1] + (c.b[1] - c.a[1]) * (c.la ?? 0.5)}
                r={Math.min(13 / pxPerM,
                            Math.hypot(c.b[0] - c.a[0], c.b[1] - c.a[1]) / 2)}
                fill="transparent" strokeWidth={0} pointerEvents="all"
                style={{ cursor: "pointer" }}
                onMouseDown={(e) => e.preventDefault()}
                onPointerDown={(e) => {
                  if (e.button !== 0) return;
                  e.stopPropagation();
                  e.preventDefault();
                  laCota(c, true);
                }} />
      ))}
      {laCota && cote.map((c, i) => (
        <circle key={`tc${i}`}
                cx={c.a[0] + (c.b[0] - c.a[0]) * (c.la ?? 0.5)}
                cy={c.a[1] + (c.b[1] - c.a[1]) * (c.la ?? 0.5)}
                r={razaCifrei(c, pxPerM, prinseAparate)}
                fill="transparent" strokeWidth={0} pointerEvents="all"
                style={{ cursor: "pointer" }}
                onMouseDown={(e) => e.preventDefault()}
                onPointerDown={(e) => {
                  if (e.button !== 0) return;
                  e.stopPropagation();
                  e.preventDefault();
                  laCota(c);
                }} />
      ))}
    </>
  );
}
